import { __assign, __extends, __awaiter, __generator, __read as __read$1, __spreadArray, __rest, __values as __values$1 } from 'tslib';
import { SamplingDecision, AlwaysOnSampler, BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { getElementXPath, WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { InstrumentationBase, registerInstrumentations } from '@opentelemetry/instrumentation';
import ErrorStackParser from 'error-stack-parser';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { onFID, onCLS, onLCP, onFCP, onINP, onTTFB } from 'web-vitals/attribution';
import { createContextKey, baggageEntryMetadataFromString, propagation, diag, trace, isSpanContextValid, TraceFlags, context } from '@opentelemetry/api';
import { B3Propagator, B3InjectEncoding } from '@opentelemetry/propagator-b3';
import { AWSXRayPropagator } from '@opentelemetry/propagator-aws-xray';

function isString(value) {
    return typeof value === 'string';
}
function isRegExp(value) {
    return value instanceof RegExp;
}
function isMatchingPattern(value, pattern, requireExactStringMatch) {
    if (requireExactStringMatch === void 0) { requireExactStringMatch = false; }
    if (!isString(value)) {
        return false;
    }
    if (isRegExp(pattern)) {
        return pattern.test(value);
    }
    if (isString(pattern)) {
        return requireExactStringMatch
            ? value === pattern
            : value.includes(pattern);
    }
    return false;
}
function stringMatchesSomePattern(testString, patterns, requireExactStringMatch) {
    if (patterns === void 0) { patterns = []; }
    if (requireExactStringMatch === void 0) { requireExactStringMatch = false; }
    return patterns.some(function (pattern) {
        return isMatchingPattern(testString, pattern, requireExactStringMatch);
    });
}
function generateUUID(placeholder) {
    return placeholder
        ? (+placeholder ^ ((Math.random() * 16) >> (+placeholder / 4))).toString(16)
        : "".concat(1e7, "-").concat(1e3, "-").concat(4e3, "-").concat(8e3, "-").concat(1e11).replace(/[018]/g, generateUUID);
}

var CoralogixEventType;
(function (CoralogixEventType) {
    CoralogixEventType["ERROR"] = "error";
    CoralogixEventType["NETWORK_REQUEST"] = "network-request";
    CoralogixEventType["LOG"] = "log";
    CoralogixEventType["USER_INTERACTION"] = "user-interaction";
    CoralogixEventType["WEB_VITALS"] = "web-vitals";
    CoralogixEventType["LONG_TASK"] = "longtask";
    CoralogixEventType["RESOURCES"] = "resources";
    CoralogixEventType["INTERNAL"] = "internal";
    CoralogixEventType["DOM"] = "dom";
    CoralogixEventType["CUSTOM_MEASUREMENT"] = "custom-measurement";
    CoralogixEventType["MEMORY_USAGE"] = "memory-usage";
    CoralogixEventType["SCREENSHOT"] = "screenshot";
    CoralogixEventType["CUSTOM_SPAN"] = "custom-span";
    CoralogixEventType["WORKER"] = "web-worker";
})(CoralogixEventType || (CoralogixEventType = {}));
var PerformanceTypes;
(function (PerformanceTypes) {
    PerformanceTypes["Resource"] = "resource";
    PerformanceTypes["LongTask"] = "longtask";
    PerformanceTypes["Navigation"] = "navigation";
    PerformanceTypes["SoftNavigation"] = "soft-navigation";
})(PerformanceTypes || (PerformanceTypes = {}));
var OtelNetworkAttrs;
(function (OtelNetworkAttrs) {
    OtelNetworkAttrs["METHOD"] = "http.method";
    OtelNetworkAttrs["URL"] = "http.url";
    OtelNetworkAttrs["STATUS_CODE"] = "http.status_code";
    OtelNetworkAttrs["HOST"] = "http.host";
    OtelNetworkAttrs["SCHEME"] = "http.scheme";
    OtelNetworkAttrs["STATUS_TEXT"] = "http.status_text";
    OtelNetworkAttrs["RESPONSE_CONTENT_LENGTH"] = "http.response_content_length";
})(OtelNetworkAttrs || (OtelNetworkAttrs = {}));
var UrlType;
(function (UrlType) {
    UrlType["PAGE"] = "page";
    UrlType["NETWORK_REQUEST"] = "network_request";
})(UrlType || (UrlType = {}));

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

function isFunction(funktion) {
  return typeof funktion === 'function';
}
var logger = console.error.bind(console);
function defineProperty(obj, name, value) {
  var enumerable = !!obj[name] && obj.propertyIsEnumerable(name);
  Object.defineProperty(obj, name, {
    configurable: true,
    enumerable: enumerable,
    writable: true,
    value: value
  });
}
function shimmer(options) {
  if (options && options.logger) {
    if (!isFunction(options.logger)) logger("new logger isn't a function, not replacing");else logger = options.logger;
  }
}
function wrap(nodule, name, wrapper) {
  if (!nodule || !nodule[name]) {
    logger('no original function ' + name + ' to wrap');
    return;
  }
  if (!wrapper) {
    logger('no wrapper function');
    logger(new Error().stack);
    return;
  }
  if (!isFunction(nodule[name]) || !isFunction(wrapper)) {
    logger('original object and wrapper must be functions');
    return;
  }
  var original = nodule[name];
  var wrapped = wrapper(original, name);
  defineProperty(wrapped, '__original', original);
  defineProperty(wrapped, '__unwrap', function () {
    if (nodule[name] === wrapped) defineProperty(nodule, name, original);
  });
  defineProperty(wrapped, '__wrapped', true);
  defineProperty(nodule, name, wrapped);
  return wrapped;
}
function massWrap(nodules, names, wrapper) {
  if (!nodules) {
    logger('must provide one or more modules to patch');
    logger(new Error().stack);
    return;
  } else if (!Array.isArray(nodules)) {
    nodules = [nodules];
  }
  if (!(names && Array.isArray(names))) {
    logger('must provide one or more functions to wrap on modules');
    return;
  }
  nodules.forEach(function (nodule) {
    names.forEach(function (name) {
      wrap(nodule, name, wrapper);
    });
  });
}
function unwrap(nodule, name) {
  if (!nodule || !nodule[name]) {
    logger('no function to unwrap.');
    logger(new Error().stack);
    return;
  }
  if (!nodule[name].__unwrap) {
    logger('no original to unwrap to -- has ' + name + ' already been unwrapped?');
  } else {
    return nodule[name].__unwrap();
  }
}
function massUnwrap(nodules, names) {
  if (!nodules) {
    logger('must provide one or more modules to patch');
    logger(new Error().stack);
    return;
  } else if (!Array.isArray(nodules)) {
    nodules = [nodules];
  }
  if (!(names && Array.isArray(names))) {
    logger('must provide one or more functions to unwrap on modules');
    return;
  }
  nodules.forEach(function (nodule) {
    names.forEach(function (name) {
      unwrap(nodule, name);
    });
  });
}
shimmer.wrap = wrap;
shimmer.massWrap = massWrap;
shimmer.unwrap = unwrap;
shimmer.massUnwrap = massUnwrap;
var shimmer_1 = shimmer;
getDefaultExportFromCjs(shimmer_1);

var CoralogixLogSeverity;
(function (CoralogixLogSeverity) {
    CoralogixLogSeverity[CoralogixLogSeverity["Debug"] = 1] = "Debug";
    CoralogixLogSeverity[CoralogixLogSeverity["Verbose"] = 2] = "Verbose";
    CoralogixLogSeverity[CoralogixLogSeverity["Info"] = 3] = "Info";
    CoralogixLogSeverity[CoralogixLogSeverity["Warn"] = 4] = "Warn";
    CoralogixLogSeverity[CoralogixLogSeverity["Error"] = 5] = "Error";
    CoralogixLogSeverity[CoralogixLogSeverity["Critical"] = 6] = "Critical";
})(CoralogixLogSeverity || (CoralogixLogSeverity = {}));

var millisecondsPerSecond = 1000;
var millisecondsPerMinute = millisecondsPerSecond * 60;
var MILLISECONDS_PER_HOUR = millisecondsPerMinute * 60;
function getNowTime() {
    return new Date().getTime();
}

var MAX_BATCH_TIME_MS = 10000;
var SESSION_RECORDING_DEFAULT_HEADERS = {
    'Content-Encoding': 'gzip',
};
var SESSION_RECORDING_DEFAULT_ERROR_MESSAGE = 'Coralogix Browser SDK - Recording is not supported on this platform';
var SESSION_RECORDING_NETWORK_ERR0R_MESSAGE = 'Coralogix Browser SDK - Error while sending record';
var BATCH_TIME_DELAY = 1000;
var SESSION_RECORDING_POSTFIX_URL = '/browser/alpha/sessionrecording';
var SESSION_KEY = 'rum_session';
var PREV_SESSION_KEY = 'rum_prev_session';
var MAX_MUTATIONS_FOR_SESSION_RECORDING = 5000;
var DEFAULT_SESSION_CONFIG = {
    sessionSampleRate: 100,
    onlyWithErrorConfig: {
        enable: false,
    },
};
var SESSION_IDLE_TIME = 15 * millisecondsPerMinute;
var SESSION_EXPIRATION_TIME = MILLISECONDS_PER_HOUR;
var SESSION_MANAGER_KEY = 'rumSessionManager';
var SESSION_RECORDER_KEY = 'rumSessionRecorder';
var SESSION_RECORDER_SEGMENTS_MAP = 'rumSessionRecorderSegmentsMap';

var SNAPSHOT_MANAGER_KEY = 'rumSnapshotManager';
var INITIAL_SNAPSHOT_CONTEXT = {
    errorCount: 0,
    viewCount: 0,
    actionCount: 0,
    hasRecording: false,
    hasScreenshot: false,
};

function getCxGlobal() {
    if (typeof globalThis === 'object') {
        return globalThis;
    }
    Object.defineProperty(Object.prototype, '__cx_global__', {
        get: function () {
            return this;
        },
        configurable: true,
    });
    var globalObject = __cx_global__;
    delete Object.prototype.__cx_global__;
    if (typeof globalObject !== 'object') {
        if (typeof self === 'object') {
            globalObject = self;
        }
        else if (typeof window === 'object') {
            globalObject = window;
        }
        else {
            globalObject = {};
        }
    }
    return globalObject;
}
var CxGlobal = getCxGlobal();

function getSnapshotManager() {
    return CxGlobal[SNAPSHOT_MANAGER_KEY];
}
function getSessionManager() {
    return CxGlobal[SESSION_MANAGER_KEY];
}
function getSessionRecorder() {
    return CxGlobal[SESSION_RECORDER_KEY];
}
function getSdkConfig() {
    return CxGlobal[SDK_CONFIG_KEY];
}
function getUserAgentData() {
    return CxGlobal[USER_AGENT_KEY];
}

var GLOBAL_SPAN_KEY = '__globalSpan__';
var GLOBAL_SPAN_MAP_KEY = '__globalSpanMap__';
var CUSTOM_TRACER_KEY = '__customTracer__';
var CUSTOM_TRACER_IGNORED_INSTRUMENTS = '__customTracerIgnoredInstruments__';

function addGlobalSpanByTraceId(traceId, globalSpan) {
    var _a;
    CxGlobal[GLOBAL_SPAN_MAP_KEY] = __assign(__assign({}, (CxGlobal[GLOBAL_SPAN_MAP_KEY] || {})), (_a = {}, _a[traceId] = globalSpan, _a));
}
function getGlobalSpanByTraceId(traceId) {
    var _a;
    return (_a = CxGlobal[GLOBAL_SPAN_MAP_KEY]) === null || _a === void 0 ? void 0 : _a[traceId];
}
function clearGlobalSpanMap() {
    var globalSpanMap = CxGlobal[GLOBAL_SPAN_MAP_KEY];
    Object.keys(globalSpanMap || {}).forEach(function (traceId) {
        var globalSpan = globalSpanMap[traceId];
        globalSpan === null || globalSpan === void 0 ? void 0 : globalSpan.endSpan();
    });
    delete CxGlobal[GLOBAL_SPAN_MAP_KEY];
}
function setGlobalSpan(globalSpan) {
    CxGlobal[GLOBAL_SPAN_KEY] = globalSpan;
}
function getGlobalSpan() {
    return CxGlobal[GLOBAL_SPAN_KEY];
}
function clearGlobalSpan() {
    delete CxGlobal[GLOBAL_SPAN_KEY];
}
function setCustomTracer(customTracer) {
    CxGlobal[CUSTOM_TRACER_KEY] = customTracer;
}
function getActiveCustomTracer() {
    return CxGlobal[CUSTOM_TRACER_KEY];
}
function setCustomTracerIgnoredInstruments(ignoredInstruments) {
    CxGlobal[CUSTOM_TRACER_IGNORED_INSTRUMENTS] = ignoredInstruments;
}
function getCustomTracerIgnoredInstruments() {
    return CxGlobal[CUSTOM_TRACER_IGNORED_INSTRUMENTS];
}
function shouldAttachSpanToGlobalSpan(instrumentationType) {
    return getGlobalSpan() && !isInstrumentationIgnored(instrumentationType);
}
function isInstrumentationIgnored(instrumentationType) {
    var _a;
    return !!((_a = getCustomTracerIgnoredInstruments()) === null || _a === void 0 ? void 0 : _a.includes(instrumentationType));
}
function attachChildSpanToGlobalSpan(name) {
    var globalSpan = getGlobalSpan();
    return globalSpan.startCustomSpan(name).span;
}

var IS_OTEL_READY = 'otelReady';
var MAIN_TRACER_KEY = '@';
var ATTR_PROCESSOR_KEY = 'attrProcessor';
var SpanStatusCode;
(function (SpanStatusCode) {
    SpanStatusCode[SpanStatusCode["UNSET"] = 0] = "UNSET";
    SpanStatusCode[SpanStatusCode["OK"] = 1] = "OK";
    SpanStatusCode[SpanStatusCode["ERROR"] = 2] = "ERROR";
})(SpanStatusCode || (SpanStatusCode = {}));

function markSpanAsOtelToSend(span) {
    span[IS_OTEL_READY] = true;
}
function shouldAddOtelAttr(span) {
    return !!span[IS_OTEL_READY];
}
function setAttrProcessor(processor) {
    CxGlobal[ATTR_PROCESSOR_KEY] = processor;
}
function getAttrProcessor() {
    return CxGlobal[ATTR_PROCESSOR_KEY];
}

function setCustomLabelsForSpan(span, labels) {
    if (labels === void 0) { labels = {}; }
    span.setAttribute(CoralogixAttributes.CUSTOM_LABELS, JSON.stringify(getCustomMergedLabels(labels)));
}
function getCustomMergedLabels(labels) {
    var _a;
    if (labels === void 0) { labels = {}; }
    var globalLabels = ((_a = getAttrProcessor()) === null || _a === void 0 ? void 0 : _a.getCustomLabels()) || {};
    return __assign(__assign({}, globalLabels), labels);
}

var CACHED_METADATA_KEY = '__cx_metadata__';
var ERROR_INSTRUMENTATION_VERSION = '1';
var STACK_LIMIT = 4096;
var MESSAGE_LIMIT = 1024;
var ERROR_INSTRUMENTATION_NAME = 'errors';
var ErrorAttributes = {
    TYPE: 'error_type',
    STACK: 'error_stack',
    MESSAGE: 'error_message',
    DATA: 'error_custom_data',
};
var ErrorSource;
(function (ErrorSource) {
    ErrorSource["CONSOLE"] = "console";
    ErrorSource["WINDOW"] = "window";
    ErrorSource["UNHANDLED_REJECTION"] = "unhandledrejection";
    ErrorSource["DOCUMENT"] = "document";
    ErrorSource["CAPTURED"] = "captured";
    ErrorSource["WEB_WORKER"] = "web_worker";
})(ErrorSource || (ErrorSource = {}));
function buildStacktrace(span, err) {
    return __awaiter(this, void 0, void 0, function () {
        var stack, message, name, hasStack, supportMfe, stackFrames;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    stack = err.stack, message = err.message, name = err.name;
                    hasStack = !!(stack === null || stack === void 0 ? void 0 : stack.includes('at '));
                    if (!(err && hasStack)) return [3, 3];
                    supportMfe = getSdkConfig().supportMfe;
                    stackFrames = ErrorStackParser.parse({
                        stack: stack.substring(0, STACK_LIMIT),
                        message: message,
                        name: name,
                    }).map(function (_a) {
                        var fileName = _a.fileName, columnNumber = _a.columnNumber, lineNumber = _a.lineNumber, functionName = _a.functionName;
                        return ({
                            fileName: fileName,
                            columnNumber: columnNumber,
                            lineNumber: lineNumber,
                            functionName: functionName,
                        });
                    });
                    if (!supportMfe) return [3, 2];
                    return [4, handleMFE(stackFrames, span)];
                case 1:
                    _a.sent();
                    _a.label = 2;
                case 2:
                    span[ErrorAttributes.STACK] = stackFrames;
                    _a.label = 3;
                case 3: return [2];
            }
        });
    });
}
function handleMFE(stackFrames, span) {
    return __awaiter(this, void 0, void 0, function () {
        var i, metadata, _a, lastFrame;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    i = 0;
                    _b.label = 1;
                case 1:
                    if (!(i < stackFrames.length)) return [3, 4];
                    return [4, extractMetadataFromMfeStacktrace(stackFrames[i].fileName)];
                case 2:
                    metadata = _b.sent();
                    stackFrames[i] = __assign(__assign({}, stackFrames[i]), (metadata || { isShell: true }));
                    _b.label = 3;
                case 3:
                    i++;
                    return [3, 1];
                case 4:
                    _a = __read$1(stackFrames, 1), lastFrame = _a[0];
                    addLabelForMFE(span, lastFrame);
                    return [2];
            }
        });
    });
}
function addLabelForMFE(span, lastFrame) {
    var app = lastFrame.app, version = lastFrame.version, isShell = lastFrame.isShell;
    if (!isShell) {
        setCustomLabelsForSpan(span, { mfeApp: app, mfeVersion: version });
    }
}
function extractMetadataFromMfeStacktrace() {
    return __awaiter(this, arguments, void 0, function (fileName) {
        var metadata, cachedMetadata, MFE_METADATA_KEY, _a, mfePath, response, _b, e_1;
        if (fileName === void 0) { fileName = ''; }
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    cachedMetadata = CxGlobal[CACHED_METADATA_KEY];
                    MFE_METADATA_KEY = 'cx-metadata.json';
                    _a = __read$1(fileName.match(/https?:\/\/.*\//), 1), mfePath = _a[0];
                    if (!mfePath) return [3, 7];
                    response = void 0;
                    if (!!(cachedMetadata === null || cachedMetadata === void 0 ? void 0 : cachedMetadata.has(mfePath))) return [3, 6];
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 4, , 5]);
                    return [4, fetch("".concat(mfePath).concat(MFE_METADATA_KEY), {
                            cache: 'no-cache',
                        })];
                case 2:
                    response = _c.sent();
                    _b = [{}];
                    return [4, response.json()];
                case 3:
                    metadata = __assign.apply(void 0, [__assign.apply(void 0, _b.concat([(_c.sent())])), { mfePath: mfePath }]);
                    return [3, 5];
                case 4:
                    e_1 = _c.sent();
                    if (getSdkConfig().debug) {
                        console.warn('Coralogix Browser SDK - Error fetching metadata', e_1);
                    }
                    return [3, 5];
                case 5:
                    CxGlobal[CACHED_METADATA_KEY] = cachedMetadata.set(mfePath, metadata);
                    return [3, 7];
                case 6:
                    metadata = cachedMetadata === null || cachedMetadata === void 0 ? void 0 : cachedMetadata.get(mfePath);
                    _c.label = 7;
                case 7: return [2, metadata];
            }
        });
    });
}
var _consoleErrorHandler = function (errorInstrumentation) {
    return function (original) {
        return function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            errorInstrumentation.report(ErrorSource.CONSOLE, args);
            return original.apply(console, args);
        };
    };
};
var _errorListener = function (that) {
    return function (event) {
        that.report(ErrorSource.WINDOW, event);
    };
};
var _unhandledRejectionListener = function (that) {
    return function (event) {
        that.report(ErrorSource.UNHANDLED_REJECTION, event.reason);
    };
};
var _documentErrorListener = function (that) {
    return function (event) {
        that.report(ErrorSource.DOCUMENT, event);
    };
};
function addErrorDefaultAttributes(span) {
    span.setAttribute(CoralogixAttributes.EVENT_TYPE, CoralogixEventType.ERROR);
    span.setAttribute(CoralogixAttributes.SEVERITY, CoralogixLogSeverity.Error);
}
var CoralogixErrorInstrumentation = (function (_super) {
    __extends(CoralogixErrorInstrumentation, _super);
    function CoralogixErrorInstrumentation(config) {
        var _this = _super.call(this, ERROR_INSTRUMENTATION_NAME, ERROR_INSTRUMENTATION_VERSION, config) || this;
        _this.parseErrorObject = function (obj) {
            var _a;
            return (_a = obj === null || obj === void 0 ? void 0 : obj.message) !== null && _a !== void 0 ? _a : JSON.stringify(obj, getCircularReplacer());
        };
        if (getSdkConfig().supportMfe) {
            CxGlobal[CACHED_METADATA_KEY] = new Map();
        }
        return _this;
    }
    CoralogixErrorInstrumentation.prototype.init = function () { };
    CoralogixErrorInstrumentation.prototype.enable = function () {
        shimmer_1.wrap(console, 'error', _consoleErrorHandler(this));
        CxGlobal.addEventListener('error', _errorListener(this));
        CxGlobal.addEventListener('unhandledrejection', _unhandledRejectionListener(this));
        document.documentElement.addEventListener('error', _documentErrorListener(this), {
            capture: true,
        });
    };
    CoralogixErrorInstrumentation.prototype.disable = function () {
        shimmer_1.unwrap(console, 'error');
        CxGlobal.removeEventListener('error', _errorListener(this));
        CxGlobal.removeEventListener('unhandledrejection', _unhandledRejectionListener(this));
        document.documentElement.removeEventListener('error', _documentErrorListener(this), { capture: true });
    };
    CoralogixErrorInstrumentation.prototype.report = function (source, arg) {
        var _this = this;
        if (arg instanceof Array) {
            if (arg.length === 0) {
                return;
            }
        }
        switch (true) {
            case arg instanceof Error:
                this.reportError(source, arg);
                break;
            case arg instanceof ErrorEvent:
                this.reportErrorEvent(source, arg);
                break;
            case arg instanceof Event:
                this.reportEvent(source, arg);
                break;
            case typeof arg === 'string':
                this.reportString(source, arg);
                break;
            case arg instanceof Array: {
                var firstError = arg.find(function (x) { return x instanceof Error; });
                var message = arg
                    .map(function (msg) {
                    return typeof msg === 'string' ? msg : _this.parseErrorObject(msg);
                })
                    .join(' ');
                this.reportString(source, message, firstError);
                break;
            }
            default:
                this.reportString(source, typeof arg === 'string' ? arg : this.getPossibleEventMessage(arg));
        }
    };
    CoralogixErrorInstrumentation.prototype.reportError = function (source, err, customData, labels) {
        return __awaiter(this, void 0, void 0, function () {
            var message, name, span, cleanMessage;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        message = err.message, name = err.name;
                        span = this.createSpan('error');
                        cleanMessage = message.split('\n')[0];
                        addErrorDefaultAttributes(span);
                        span.setAttribute(CoralogixAttributes.SOURCE, source);
                        span.setAttribute(ErrorAttributes.TYPE, name);
                        span.setAttribute(ErrorAttributes.MESSAGE, cleanMessage);
                        if (customData) {
                            span[ErrorAttributes.DATA] = JSON.stringify(customData, getCircularReplacer());
                        }
                        if (labels) {
                            setCustomLabelsForSpan(span, labels);
                        }
                        return [4, buildStacktrace(span, err)];
                    case 1:
                        _a.sent();
                        span.end();
                        return [2];
                }
            });
        });
    };
    CoralogixErrorInstrumentation.prototype.reportString = function (source, message, firstError) {
        return __awaiter(this, void 0, void 0, function () {
            var span;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        span = this.createSpan(source);
                        addErrorDefaultAttributes(span);
                        span.setAttribute(CoralogixAttributes.SOURCE, source);
                        span.setAttribute(ErrorAttributes.MESSAGE, message === null || message === void 0 ? void 0 : message.substring(0, MESSAGE_LIMIT));
                        if (!firstError) return [3, 2];
                        return [4, buildStacktrace(span, firstError)];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2:
                        span.end();
                        return [2];
                }
            });
        });
    };
    CoralogixErrorInstrumentation.prototype.reportErrorEvent = function (source, _a) {
        var error = _a.error, message = _a.message;
        var event = error !== null && error !== void 0 ? error : message;
        this.report(source, event);
    };
    CoralogixErrorInstrumentation.prototype.reportEvent = function (source, ev) {
        if (!ev.target) {
            return;
        }
        var span = this.createSpan(source);
        addErrorDefaultAttributes(span);
        span.setAttribute(CoralogixAttributes.SOURCE, source);
        if (ev.target) {
            span.setAttribute('target_element', ev.target.tagName);
            span.setAttribute('target_xpath', getElementXPath(ev.target, true));
            span.setAttribute('target_src', ev.target.src);
        }
        span.end();
    };
    CoralogixErrorInstrumentation.prototype.createSpan = function (name) {
        var span;
        if (shouldAttachSpanToGlobalSpan(CoralogixEventType.ERROR)) {
            span = attachChildSpanToGlobalSpan(name);
        }
        else {
            span = this.tracer.startSpan(name);
        }
        return span;
    };
    CoralogixErrorInstrumentation.prototype.getPossibleEventMessage = function (event) {
        var _a = event || {}, message = _a.message, statusMessage = _a.statusMessage;
        if (message && typeof message === 'string') {
            return message;
        }
        if (statusMessage && typeof statusMessage === 'string') {
            return statusMessage;
        }
        return JSON.stringify(event);
    };
    return CoralogixErrorInstrumentation;
}(InstrumentationBase));

var propagateTraceHeaderCorsUrls = function (config) {
    var _a;
    var traceParentInHeader = config.traceParentInHeader;
    var propagateCorsUrls = (_a = traceParentInHeader === null || traceParentInHeader === void 0 ? void 0 : traceParentInHeader.options) === null || _a === void 0 ? void 0 : _a.propagateTraceHeaderCorsUrls;
    if ((traceParentInHeader === null || traceParentInHeader === void 0 ? void 0 : traceParentInHeader.enabled) && propagateCorsUrls) {
        config.propagateTraceHeaderCorsUrls = propagateCorsUrls;
    }
};

var NetworkDataManager = (function () {
    function NetworkDataManager() {
        var _this = this;
        this.responseBodyParsers = new Map([
            [MimeType.Json, function (body) { return __awaiter(_this, void 0, void 0, function () { var _a, _b; return __generator(this, function (_c) {
                    switch (_c.label) {
                        case 0:
                            _b = (_a = JSON).stringify;
                            return [4, body.json()];
                        case 1: return [2, _b.apply(_a, [_c.sent()])];
                    }
                }); }); }],
            [MimeType.TextPlain, function (body) { return __awaiter(_this, void 0, void 0, function () { return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0: return [4, body.text()];
                        case 1: return [2, _a.sent()];
                    }
                }); }); }],
            [MimeType.TextHtml, function (body) { return __awaiter(_this, void 0, void 0, function () { return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0: return [4, body.text()];
                        case 1: return [2, _a.sent()];
                    }
                }); }); }],
            [MimeType.TextCss, function (body) { return __awaiter(_this, void 0, void 0, function () { return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0: return [4, body.text()];
                        case 1: return [2, _a.sent()];
                    }
                }); }); }],
            [MimeType.TextJavascript, function (body) { return __awaiter(_this, void 0, void 0, function () { return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0: return [4, body.text()];
                        case 1: return [2, _a.sent()];
                    }
                }); }); }],
            [MimeType.Xml, function (body) { return __awaiter(_this, void 0, void 0, function () { return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0: return [4, body.text()];
                        case 1: return [2, _a.sent()];
                    }
                }); }); }],
            [MimeType.FormData, function (body) { return __awaiter(_this, void 0, void 0, function () { return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0: return [4, body.text()];
                        case 1: return [2, _a.sent()];
                    }
                }); }); }],
        ]);
        this.requestBodySerializers = new Map([
            [BodyType.String, function (body) { return __awaiter(_this, void 0, void 0, function () { return __generator(this, function (_a) {
                    return [2, body];
                }); }); }],
            [BodyType.Blob, function (body) { return __awaiter(_this, void 0, void 0, function () { return __generator(this, function (_a) {
                    return [2, body.text()];
                }); }); }],
            [
                BodyType.FormData,
                function (body) { return __awaiter(_this, void 0, void 0, function () { return __generator(this, function (_a) {
                    return [2, this.serializeFormData(body)];
                }); }); },
            ],
            [
                BodyType.URLSearchParams,
                function (body) { return __awaiter(_this, void 0, void 0, function () { return __generator(this, function (_a) {
                    return [2, body.toString()];
                }); }); },
            ],
            [BodyType.JsonObject, function (body) { return __awaiter(_this, void 0, void 0, function () { return __generator(this, function (_a) {
                    return [2, JSON.stringify(body)];
                }); }); }],
        ]);
    }
    NetworkDataManager.prototype.resolveConfigForUrl = function (url, configs) {
        if (configs === void 0) { configs = []; }
        return configs.find(function (_a) {
            var configUrl = _a.url;
            return configUrl === url ||
                (configUrl instanceof RegExp && configUrl.test(url));
        });
    };
    NetworkDataManager.prototype.stringifyRequestBody = function (request) {
        return __awaiter(this, void 0, void 0, function () {
            var bodyContent;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(request === null || request === void 0 ? void 0 : request.body))
                            return [2, ''];
                        return [4, this.serializeRequestBody(request.body)];
                    case 1:
                        bodyContent = _a.sent();
                        return [2, this.truncateIfExceedsLimit(bodyContent)];
                }
            });
        });
    };
    NetworkDataManager.prototype.stringifyResponseBody = function (response) {
        return __awaiter(this, void 0, void 0, function () {
            var contentType, mimeType, parser, parsedContent;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        contentType = response.headers.get('content-type') || '';
                        mimeType = contentType.split(';')[0].trim();
                        parser = this.responseBodyParsers.get(mimeType);
                        if (!parser)
                            return [2, ''];
                        return [4, parser(response)];
                    case 1:
                        parsedContent = _a.sent();
                        return [2, this.truncateIfExceedsLimit(parsedContent)];
                }
            });
        });
    };
    NetworkDataManager.prototype.truncateIfExceedsLimit = function (content) {
        return content.length > MAX_CHARACTERS ? undefined : content;
    };
    NetworkDataManager.prototype.serializeRequestBody = function (body) {
        return __awaiter(this, void 0, void 0, function () {
            var handler, _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        handler = this.requestBodySerializers.get(body.constructor.name);
                        if (!handler) return [3, 2];
                        return [4, handler(body)];
                    case 1:
                        _a = _b.sent();
                        return [3, 3];
                    case 2:
                        _a = '';
                        _b.label = 3;
                    case 3: return [2, _a];
                }
            });
        });
    };
    NetworkDataManager.prototype.serializeFormData = function (formData) {
        var result = '';
        formData.forEach(function (value, key) {
            result += "".concat(key, ": ").concat(value, "\n");
        });
        return result.trim();
    };
    return NetworkDataManager;
}());
var MimeType;
(function (MimeType) {
    MimeType["Json"] = "application/json";
    MimeType["TextPlain"] = "text/plain";
    MimeType["TextHtml"] = "text/html";
    MimeType["TextCss"] = "text/css";
    MimeType["TextJavascript"] = "application/javascript";
    MimeType["Xml"] = "application/xml";
    MimeType["FormData"] = "multipart/form-data";
})(MimeType || (MimeType = {}));
var BodyType;
(function (BodyType) {
    BodyType["String"] = "String";
    BodyType["Blob"] = "Blob";
    BodyType["FormData"] = "FormData";
    BodyType["URLSearchParams"] = "URLSearchParams";
    BodyType["JsonObject"] = "JsonObject";
    BodyType["Unknown"] = "Unknown";
})(BodyType || (BodyType = {}));

var HeadersManager = (function () {
    function HeadersManager() {
    }
    HeadersManager.prototype.filterHeaders = function (headersInit, allowedKeys) {
        if (allowedKeys === void 0) { allowedKeys = []; }
        if (!headersInit) {
            return new Map();
        }
        var normalized = this.normalizeHeaders(headersInit);
        var filteredHeaders = new Map();
        allowedKeys.forEach(function (headerKey) {
            var normalizedKey = headerKey.toLowerCase();
            if (normalized.has(normalizedKey)) {
                filteredHeaders.set(headerKey, normalized.get(normalizedKey));
            }
        });
        return filteredHeaders;
    };
    HeadersManager.prototype.normalizeHeaders = function (headersInit) {
        if (typeof headersInit === 'string') {
            return this.normalizeHeadersFromString(headersInit);
        }
        var headers = new Headers(headersInit);
        var headersMap = new Map();
        headers.forEach(function (value, key) {
            headersMap.set(key.toLowerCase(), value);
        });
        return headersMap;
    };
    HeadersManager.prototype.normalizeHeadersFromString = function (headersString) {
        var headerMap = new Map();
        if (!headersString) {
            return headerMap;
        }
        var lines = headersString.trim().split(/\r\n/);
        lines.forEach(function (line) {
            var _a = __read$1(line.split(': ', 2), 2), key = _a[0], value = _a[1];
            if (key && value) {
                headerMap.set(key.trim().toLowerCase(), value.trim());
            }
        });
        return headerMap;
    };
    return HeadersManager;
}());

var NetworkInstrumentationAttributes = {
    REQUEST_HEADERS: 'request_headers',
    RESPONSE_HEADERS: 'response_headers',
    RESPONSE_PAYLOAD: 'response_payload',
    REQUEST_PAYLOAD: 'request_payload',
};

function checkAttachingNetworkSpanToGlobalSpan(span) {
    var activeGlobalSpan = getGlobalSpanByTraceId(span.spanContext().traceId);
    if (activeGlobalSpan &&
        !isInstrumentationIgnored(CoralogixEventType.NETWORK_REQUEST)) {
        var _a = activeGlobalSpan.span.spanContext(), traceId = _a.traceId, globalSpanId = _a.spanId;
        span['_spanContext'].traceId = traceId;
        span['parentSpanId'] = globalSpanId;
    }
}

var FetchSource;
(function (FetchSource) {
    FetchSource["FETCH"] = "fetch";
})(FetchSource || (FetchSource = {}));
var CoralogixFetchInstrumentation = (function (_super) {
    __extends(CoralogixFetchInstrumentation, _super);
    function CoralogixFetchInstrumentation(config) {
        var _this = this;
        propagateTraceHeaderCorsUrls(config);
        config.applyCustomAttributesOnSpan = function (span, request, result) {
            markSpanAsOtelToSend(span);
            checkAttachingNetworkSpanToGlobalSpan(span);
            _this.setNetworkSpanAttributes(span, request, result);
        };
        _this = _super.call(this, config) || this;
        _this.networkDataManager = new NetworkDataManager();
        _this.headersManager = new HeadersManager();
        return _this;
    }
    CoralogixFetchInstrumentation.prototype.setNetworkSpanAttributes = function (span, request, response) {
        return __awaiter(this, void 0, void 0, function () {
            var status, networkExtraConfig, url, resolvedConfig;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        status = response.status;
                        networkExtraConfig = getSdkConfig().networkExtraConfig;
                        url = span.attributes['http.url'] ||
                            '';
                        resolvedConfig = this.networkDataManager.resolveConfigForUrl(url, networkExtraConfig);
                        if (!resolvedConfig) return [3, 3];
                        return [4, this.setRequestAttributes(span, request, resolvedConfig)];
                    case 1:
                        _b.sent();
                        return [4, this.setResponseAttributes(span, response, resolvedConfig)];
                    case 2:
                        _b.sent();
                        _b.label = 3;
                    case 3:
                        span.setAttributes((_a = {},
                            _a[CoralogixAttributes.SEVERITY] = isNetworkError(status)
                                ? CoralogixLogSeverity.Error
                                : CoralogixLogSeverity.Info,
                            _a[CoralogixAttributes.EVENT_TYPE] = CoralogixEventType.NETWORK_REQUEST,
                            _a[CoralogixAttributes.SOURCE] = FetchSource.FETCH,
                            _a));
                        return [2];
                }
            });
        });
    };
    CoralogixFetchInstrumentation.prototype.setRequestAttributes = function (span, request, networkExtraConfig) {
        return __awaiter(this, void 0, void 0, function () {
            var reqHeaders, collectReqPayload, requestPayload, _a, sanitizedHeaders, error_1;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        reqHeaders = networkExtraConfig.reqHeaders, collectReqPayload = networkExtraConfig.collectReqPayload;
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 5, , 6]);
                        if (!collectReqPayload) return [3, 3];
                        return [4, this.networkDataManager.stringifyRequestBody(request)];
                    case 2:
                        _a = _b.sent();
                        return [3, 4];
                    case 3:
                        _a = undefined;
                        _b.label = 4;
                    case 4:
                        requestPayload = _a;
                        sanitizedHeaders = (reqHeaders === null || reqHeaders === void 0 ? void 0 : reqHeaders.length)
                            ? this.headersManager.filterHeaders(request.headers, reqHeaders)
                            : undefined;
                        if (requestPayload)
                            span[NetworkInstrumentationAttributes.REQUEST_PAYLOAD] = requestPayload;
                        if (sanitizedHeaders === null || sanitizedHeaders === void 0 ? void 0 : sanitizedHeaders.size) {
                            span[NetworkInstrumentationAttributes.REQUEST_HEADERS] =
                                Object.fromEntries(sanitizedHeaders);
                        }
                        return [3, 6];
                    case 5:
                        error_1 = _b.sent();
                        if (getSdkConfig().debug) {
                            console.debug('Error setting request attributes on span:', error_1);
                        }
                        return [3, 6];
                    case 6: return [2];
                }
            });
        });
    };
    CoralogixFetchInstrumentation.prototype.setResponseAttributes = function (span, result, networkExtraConfig) {
        return __awaiter(this, void 0, void 0, function () {
            var resHeaders, collectResPayload, responsePayload, _a, sanitizedHeaders, error_2;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        resHeaders = networkExtraConfig.resHeaders, collectResPayload = networkExtraConfig.collectResPayload;
                        if (!(result instanceof Response)) return [3, 6];
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 5, , 6]);
                        if (!collectResPayload) return [3, 3];
                        return [4, this.networkDataManager.stringifyResponseBody(result)];
                    case 2:
                        _a = _b.sent();
                        return [3, 4];
                    case 3:
                        _a = undefined;
                        _b.label = 4;
                    case 4:
                        responsePayload = _a;
                        sanitizedHeaders = (resHeaders === null || resHeaders === void 0 ? void 0 : resHeaders.length)
                            ? this.headersManager.filterHeaders(result.headers, resHeaders)
                            : undefined;
                        if (responsePayload) {
                            span[NetworkInstrumentationAttributes.RESPONSE_PAYLOAD] =
                                responsePayload;
                        }
                        if (sanitizedHeaders === null || sanitizedHeaders === void 0 ? void 0 : sanitizedHeaders.size) {
                            span[NetworkInstrumentationAttributes.RESPONSE_HEADERS] =
                                Object.fromEntries(sanitizedHeaders);
                        }
                        return [3, 6];
                    case 5:
                        error_2 = _b.sent();
                        if (getSdkConfig().debug) {
                            console.debug('Error setting request attributes on span:', error_2);
                        }
                        return [3, 6];
                    case 6: return [2];
                }
            });
        });
    };
    CoralogixFetchInstrumentation.prototype.disable = function () { };
    return CoralogixFetchInstrumentation;
}(FetchInstrumentation));

var XHRSource;
(function (XHRSource) {
    XHRSource["XHR"] = "xhr";
})(XHRSource || (XHRSource = {}));
var CoralogixXhrInstrumentation = (function (_super) {
    __extends(CoralogixXhrInstrumentation, _super);
    function CoralogixXhrInstrumentation(config) {
        var _this = this;
        propagateTraceHeaderCorsUrls(config);
        config.applyCustomAttributesOnSpan = function (span, xhr) {
            markSpanAsOtelToSend(span);
            checkAttachingNetworkSpanToGlobalSpan(span);
            _this.setNetworkSpanAttributes(span, xhr);
        };
        _this = _super.call(this, config) || this;
        _this.networkDataManager = new NetworkDataManager();
        _this.headersManager = new HeadersManager();
        return _this;
    }
    CoralogixXhrInstrumentation.prototype.setNetworkSpanAttributes = function (span, xhr) {
        var _a;
        var status = xhr.status;
        var networkExtraConfig = getSdkConfig().networkExtraConfig;
        var resolvedConfig = this.networkDataManager.resolveConfigForUrl(xhr.responseURL, networkExtraConfig);
        span.setAttributes((_a = {},
            _a[CoralogixAttributes.SEVERITY] = isNetworkError(status)
                ? CoralogixLogSeverity.Error
                : CoralogixLogSeverity.Info,
            _a[CoralogixAttributes.EVENT_TYPE] = CoralogixEventType.NETWORK_REQUEST,
            _a[CoralogixAttributes.SOURCE] = XHRSource.XHR,
            _a));
        if (resolvedConfig) {
            this.setResponseAttributes(span, xhr, resolvedConfig);
        }
    };
    CoralogixXhrInstrumentation.prototype.setResponseAttributes = function (span, xhr, resolvedConfig) {
        var resHeaders = resolvedConfig.resHeaders, collectResPayload = resolvedConfig.collectResPayload;
        var responsePayload = collectResPayload
            ? this.networkDataManager.truncateIfExceedsLimit(xhr.responseText)
            : undefined;
        var sanitizedHeaders = (resHeaders === null || resHeaders === void 0 ? void 0 : resHeaders.length)
            ? this.headersManager.filterHeaders(xhr.getAllResponseHeaders(), resHeaders)
            : undefined;
        if (responsePayload) {
            span[NetworkInstrumentationAttributes.RESPONSE_PAYLOAD] = responsePayload;
        }
        if (sanitizedHeaders === null || sanitizedHeaders === void 0 ? void 0 : sanitizedHeaders.size) {
            span[NetworkInstrumentationAttributes.RESPONSE_HEADERS] =
                Object.fromEntries(sanitizedHeaders);
        }
    };
    CoralogixXhrInstrumentation.prototype.disable = function () { };
    return CoralogixXhrInstrumentation;
}(XMLHttpRequestInstrumentation));

var CUSTOM_INSTRUMENTATION_VERSION = '1';
var LogSource;
(function (LogSource) {
    LogSource["CODE"] = "code";
})(LogSource || (LogSource = {}));
var CoralogixCustomLogInstrumentation = (function (_super) {
    __extends(CoralogixCustomLogInstrumentation, _super);
    function CoralogixCustomLogInstrumentation(config) {
        var _a;
        var _this = _super.call(this, CoralogixEventType.LOG, CUSTOM_INSTRUMENTATION_VERSION, config) || this;
        _this.stringifyCustomLogData = (_a = getSdkConfig()) === null || _a === void 0 ? void 0 : _a.stringifyCustomLogData;
        return _this;
    }
    CoralogixCustomLogInstrumentation.prototype.init = function () { };
    CoralogixCustomLogInstrumentation.prototype.disable = function () { };
    CoralogixCustomLogInstrumentation.prototype.enable = function () { };
    CoralogixCustomLogInstrumentation.prototype.log = function (_a) {
        var severity = _a.severity, message = _a.message, data = _a.data, labels = _a.labels;
        var span = this.tracer.startSpan(CoralogixEventType.LOG);
        span.setAttribute(CoralogixAttributes.EVENT_TYPE, CoralogixEventType.LOG);
        span.setAttribute(CoralogixAttributes.SOURCE, LogSource.CODE);
        span.setAttribute(CoralogixAttributes.SEVERITY, severity);
        span.setAttribute(CoralogixAttributes.LOG, JSON.stringify({
            severity: severity,
            message: message,
            data: this.stringifyCustomLogData
                ? JSON.stringify(data, getCircularReplacer())
                : data,
        }));
        if (labels) {
            setCustomLabelsForSpan(span, labels);
        }
        span.end();
    };
    return CoralogixCustomLogInstrumentation;
}(InstrumentationBase));

function isNil(value) {
    return value == null;
}

function coerceArray(value) {
    if (isNil(value))
        return [];
    if (value instanceof Set)
        return __spreadArray([], __read$1(value), false);
    return Array.isArray(value) ? value : [value];
}

function isObject(value) {
    return typeof value === 'object';
}
function omit(object) {
    if (object === void 0) { object = {}; }
    var paths = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        paths[_i - 1] = arguments[_i];
    }
    if (isNil(object))
        return {};
    var omitter = function (source, pathArray) {
        var _a;
        var _b = __read$1(pathArray), firstKey = _b[0], remainingKeys = _b.slice(1);
        if (!source.hasOwnProperty(firstKey))
            return source;
        if (!remainingKeys.length) {
            var _c = source, _d = firstKey; _c[_d]; var rest = __rest(_c, [typeof _d === "symbol" ? _d : _d + ""]);
            return rest;
        }
        return __assign(__assign({}, source), (_a = {}, _a[firstKey] = omitter(source[firstKey], remainingKeys), _a));
    };
    return coerceArray(paths).reduce(function (result, path) { return omitter(result, path.split('.')); }, object);
}
function isEmpty(value) {
    if (isNil(value))
        return true;
    if (value instanceof Set || value instanceof Map) {
        return value.size === 0;
    }
    if (Array.isArray(value) || typeof value === 'string') {
        return value.length === 0;
    }
    if (isObject(value)) {
        return Object.keys(value).length === 0;
    }
    return false;
}
function isNotEmpty(value) {
    return !isEmpty(value);
}

var WEB_VITALS_INSTRUMENTATION_NAME = 'web_vitals';
var WEB_VITALS_INSTRUMENTATION_VERSION = '1.0.0';
var LONG_TASK_INSTRUMENTATION_VERSION = '1.0.0';
var LONG_TASKS_INSTRUMENTATION_NAME = 'long_tasks';
var RESOURCES_INSTRUMENTATION_NAME = 'resources';
var RESOURCES_INSTRUMENTATION_VERSION = '1.0.0';
var DOM_INSTRUMENTATION_NAME = 'dom';
var DOM_INSTRUMENTATION_VERSION = '1.0.0';
var CUSTOM_MEASUREMENT_INSTRUMENTATION_NAME = 'custom_measurement';
var CUSTOM_MEASUREMENT_INSTRUMENTATION_VERSION = '1.0.0';
var WORKER_INSTRUMENTATION = 'web_worker';
var WORKER_INSTRUMENTATION_VERSION = '1.0.0';
var XHR_INSTRUMENTATION_NAME = 'xhr';
var FETCH_INSTRUMENTATION_NAME = 'fetch';
var CUSTOM_INSTRUMENTATION_NAME = 'custom';
var SESSION_RECORDING_INSTRUMENTATION_NAME = 'session_recording';
var ALL_WEB_VITALS_METRICS = {
    lcp: true,
    fid: true,
    cls: true,
    fcp: true,
    inp: true,
    ttfb: true,
    tbt: true,
    lt: true,
};
var LONGTASK_DURATION = 50;
var INTERNAL_INSTRUMENTATION_NAME = 'internal';
var FONTS_URL = 'fonts.googleapis.com';
var CSS_EXTENSIONS = ['.css'];
var OTHER_EXTENSIONS = ['.wasm', '.manifest'];
var SCRIPT_EXTENSIONS = ['.js', '.mjs'];
var MEDIA_EXTENSIONS = [
    '.mp3',
    '.mp4',
    '.wav',
    '.ogg',
    '.webm',
    '.avi',
];
var DOC_EXTENSIONS = [
    '.pdf',
    '.doc',
    '.docx',
    '.ppt',
    '.pptx',
    '.xls',
    '.xlsx',
    '.csv',
    '.html',
];
var FONT_EXTENSIONS = [
    '.woff',
    '.woff2',
    '.ttf',
    '.otf',
    '.eot',
];
var IMG_EXTENSIONS = [
    '.svg',
    '.jpg',
    '.png',
    '.jpeg',
    '.bmp',
    '.webp',
];

/** Original source: https://www.npmjs.com/package/tti-polyfill?activeTab=code
 * @license Apache-2.0
 * @version 0.2.2
 * @description A polyfill for the Time to Interactive metric -
 * We decide to fork this package instead of installing it from npm
 * because it's not maintained anymore, and we want to have control over the codebase.
 * Also, we don't want to have a dependency on a package that is not maintained.
 */

var h = 'undefined' != typeof CxGlobal ? CxGlobal : {},
  k = 'function' == typeof Object.defineProperties ? Object.defineProperty : function (a, b, c) {
    a != Array.prototype && a != Object.prototype && (a[b] = c.value);
  };
function l() {
  l = function l() {};
  h.Symbol || (h.Symbol = m);
}
var n = 0;
function m(a) {
  return 'jscomp_symbol_' + (a || '') + n++;
}
function p() {
  l();
  var a = h.Symbol.iterator;
  a || (a = h.Symbol.iterator = h.Symbol('iterator'));
  'function' != typeof Array.prototype[a] && k(Array.prototype, a, {
    configurable: true,
    writable: true,
    value: function value() {
      return q(this);
    }
  });
  p = function p() {};
}
function q(a) {
  var b = 0;
  return r(function () {
    return b < a.length ? {
      done: false,
      value: a[b++]
    } : {
      done: true
    };
  });
}
function r(a) {
  p();
  a = {
    next: a
  };
  a[h.Symbol.iterator] = function () {
    return this;
  };
  return a;
}
function t(a) {
  p();
  var b = a[Symbol.iterator];
  return b ? b.call(a) : q(a);
}
function u(a) {
  if (!(a instanceof Array)) {
    a = t(a);
    for (var b, c = []; !(b = a.next()).done;) c.push(b.value);
    a = c;
  }
  return a;
}
var v = 0;
function w(a, b) {
  var c = XMLHttpRequest.prototype.send,
    d = v++;
  XMLHttpRequest.prototype.send = function (f) {
    for (var e = [], g = 0; g < arguments.length; ++g) e[g - 0] = arguments[g];
    var E = this;
    a(d);
    this.addEventListener('readystatechange', function () {
      4 === E.readyState && b(d);
    });
    return c.apply(this, e);
  };
}
function x(a, b) {
  var c = fetch;
  fetch = function fetch(d) {
    for (var f = [], e = 0; e < arguments.length; ++e) f[e - 0] = arguments[e];
    return new Promise(function (d, e) {
      var g = v++;
      a(g);
      c.apply(null, [].concat(u(f))).then(function (a) {
        b(g);
        d(a);
      }, function (a) {
        b(a);
        e(a);
      });
    });
  };
}
var y = 'img script iframe link audio video source'.split(' ');
function z(a, b) {
  a = t(a);
  for (var c = a.next(); !c.done; c = a.next()) if (c = c.value, b.includes(c.nodeName.toLowerCase()) || z(c.children, b)) return true;
  return false;
}
function A(a) {
  var b = new MutationObserver(function (c) {
    c = t(c);
    for (var b = c.next(); !b.done; b = c.next()) b = b.value, 'childList' == b.type && z(b.addedNodes, y) ? a(b) : 'attributes' == b.type && y.includes(b.target.tagName.toLowerCase()) && a(b);
  });
  b.observe(document, {
    attributes: true,
    childList: true,
    subtree: true,
    attributeFilter: ['href', 'src']
  });
  return b;
}
function B(a, b) {
  if (2 < a.length) return performance.now();
  var c = [];
  b = t(b);
  for (var d = b.next(); !d.done; d = b.next()) d = d.value, c.push({
    timestamp: d.start,
    type: 'requestStart'
  }), c.push({
    timestamp: d.end,
    type: 'requestEnd'
  });
  b = t(a);
  for (d = b.next(); !d.done; d = b.next()) c.push({
    timestamp: d.value,
    type: 'requestStart'
  });
  c.sort(function (a, b) {
    return a.timestamp - b.timestamp;
  });
  a = a.length;
  for (b = c.length - 1; 0 <= b; b--) switch (d = c[b], d.type) {
    case 'requestStart':
      a--;
      break;
    case 'requestEnd':
      a++;
      if (2 < a) return d.timestamp;
      break;
    default:
      throw Error('Internal Error: This should never happen');
  }
  return 0;
}
function C(a) {
  a = a ? a : {};
  this.w = !!a.useMutationObserver;
  this.u = a.minValue || null;
  a = CxGlobal.__tti && CxGlobal.__tti.e;
  var b = CxGlobal.__tti && CxGlobal.__tti.o;
  this.a = a ? a.map(function (a) {
    return {
      start: a.startTime,
      end: a.startTime + a.duration
    };
  }) : [];
  b && b.disconnect();
  this.b = [];
  this.f = new Map();
  this.j = null;
  this.v = -Infinity;
  this.i = false;
  this.h = this.c = this.s = null;
  w(this.m.bind(this), this.l.bind(this));
  x(this.m.bind(this), this.l.bind(this));
  D(this);
  this.w && (this.h = A(this.B.bind(this)));
}
C.prototype.getFirstConsistentlyInteractive = function () {
  var a = this;
  return new Promise(function (b) {
    a.s = b;
    'complete' == document.readyState ? F(a) : CxGlobal.addEventListener('load', function () {
      F(a);
    });
  });
};
function F(a) {
  a.i = true;
  var b = 0 < a.a.length ? a.a[a.a.length - 1].end : 0,
    c = B(a.g, a.b);
  G(a, Math.max(c + 5e3, b));
}
function G(a, b) {
  !a.i || a.v > b || (clearTimeout(a.j), a.j = setTimeout(function () {
    var b = performance.timing.navigationStart,
      d = B(a.g, a.b),
      b = (CxGlobal.a && CxGlobal.a.A ? 1e3 * CxGlobal.a.A().C - b : 0) || performance.timing.domContentLoadedEventEnd - b;
    if (a.u) var f = a.u;else performance.timing.domContentLoadedEventEnd ? (f = performance.timing, f = f.domContentLoadedEventEnd - f.navigationStart) : f = null;
    var e = performance.now();
    null === f && G(a, Math.max(d + 5e3, e + 1e3));
    var g = a.a;
    5e3 > e - d ? d = null : (d = g.length ? g[g.length - 1].end : b, d = 5e3 > e - d ? null : Math.max(d, f));
    d && (a.s(d), clearTimeout(a.j), a.i = false, a.c && a.c.disconnect(), a.h && a.h.disconnect());
    G(a, performance.now() + 1e3);
  }, b - performance.now()), a.v = b);
}
function D(a) {
  a.c = new PerformanceObserver(function (b) {
    b = t(b.getEntries());
    for (var c = b.next(); !c.done; c = b.next()) if (c = c.value, 'resource' === c.entryType && (a.b.push({
      start: c.fetchStart,
      end: c.responseEnd
    }), G(a, B(a.g, a.b) + 5e3)), 'longtask' === c.entryType) {
      var d = c.startTime + c.duration;
      a.a.push({
        start: c.startTime,
        end: d
      });
      G(a, d + 5e3);
    }
  });
  a.c.observe({
    entryTypes: ['longtask', 'resource']
  });
}
C.prototype.m = function (a) {
  this.f.set(a, performance.now());
};
C.prototype.l = function (a) {
  this.f["delete"](a);
};
C.prototype.B = function () {
  G(this, performance.now() + 5e3);
};
h.Object.defineProperties(C.prototype, {
  g: {
    configurable: true,
    enumerable: true,
    get: function get() {
      return [].concat(u(this.f.values()));
    }
  }
});
var H = {
  getFirstConsistentlyInteractive: function getFirstConsistentlyInteractive() {
    var a = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : undefined;
    a = a ? a : {};
    return 'PerformanceLongTaskTiming' in CxGlobal ? new C(a).getFirstConsistentlyInteractive() : Promise.resolve(null);
  }
};

function observeIfSupported(observer, entryType) {
    if (!isPerformanceObserverSupported()) {
        return;
    }
    try {
        observer.observe({ type: entryType, buffered: true });
    }
    catch (error) {
        observer.observe({ entryTypes: [entryType] });
    }
}
function isPerformanceObserverSupported() {
    var _a;
    if (!((_a = CxGlobal === null || CxGlobal === void 0 ? void 0 : CxGlobal.PerformanceObserver) === null || _a === void 0 ? void 0 : _a.supportedEntryTypes)) {
        console.warn('Coralogix Browser SDK  - browser does not support the required APIs ');
        return false;
    }
    return true;
}

var CoralogixWebVitalsInstrumentation = (function (_super) {
    __extends(CoralogixWebVitalsInstrumentation, _super);
    function CoralogixWebVitalsInstrumentation(config) {
        var _this = _super.call(this, WEB_VITALS_INSTRUMENTATION_NAME, WEB_VITALS_INSTRUMENTATION_VERSION, config) || this;
        _this.observers = [];
        _this.isSoftNavsEnabled = getSdkConfig().trackSoftNavigations;
        _this.onReport = function (metric) {
            if (!metric) {
                return;
            }
            if (metric.name === 'FCP') {
                _this.registerToTBTMetric();
            }
            var webVitalsSpan = _this.tracer.startSpan(CoralogixEventType.WEB_VITALS);
            var metricData = metric.entries ? omit(metric, 'entries') : metric;
            metricData = omit(metricData, 'attribution.navigationEntry');
            if (metricData.name === 'INP') {
                metricData = omit(metricData, 'attribution');
            }
            webVitalsSpan.setAttribute(CoralogixAttributes.EVENT_TYPE, CoralogixEventType.WEB_VITALS);
            webVitalsSpan.setAttribute(WEB_VITALS_INSTRUMENTATION_NAME, JSON.stringify(metricData, getCircularReplacer()));
            webVitalsSpan.end();
        };
        _this.metrics = isEmpty(config.metrics)
            ? ALL_WEB_VITALS_METRICS
            : config.metrics;
        if (!isPerformanceObserverSupported()) {
            return _this;
        }
        _this.registerToCoreWebVitalMetrics();
        _this.registerToCalculatedWebVitalMetrics();
        if (_this.isSoftNavsEnabled) {
            _this.registerToSoftNavigations();
        }
        return _this;
    }
    CoralogixWebVitalsInstrumentation.prototype.registerToCoreWebVitalMetrics = function () {
        var shouldReportSoftNavs = this.isSoftNavsEnabled &&
            supportedPerformanceTypes.has(PerformanceTypes.SoftNavigation);
        if (this.metrics.fid) {
            onFID(this.onReport);
        }
        if (this.metrics.cls) {
            onCLS(this.onReport, { reportSoftNavs: shouldReportSoftNavs });
        }
        if (this.metrics.lcp) {
            onLCP(this.onReport, { reportSoftNavs: shouldReportSoftNavs });
        }
        if (this.metrics.fcp) {
            onFCP(this.onReport, { reportSoftNavs: shouldReportSoftNavs });
        }
        if (this.metrics.inp) {
            onINP(this.onReport, { reportSoftNavs: shouldReportSoftNavs });
        }
        if (this.metrics.ttfb) {
            onTTFB(this.onReport, { reportSoftNavs: shouldReportSoftNavs });
        }
    };
    CoralogixWebVitalsInstrumentation.prototype.registerToSoftNavigations = function () {
        var _this = this;
        if (supportedPerformanceTypes.has(PerformanceTypes.SoftNavigation)) {
            var softNavObserver = new PerformanceObserver(function (list) {
                var softNavMetric;
                list.getEntries().forEach(function (entry) {
                    var duration = entry.duration, name = entry.name, startTime = entry.startTime, navigationId = entry.navigationId;
                    softNavMetric = {
                        name: 'SN',
                        id: generateWebVitalUniqueID(),
                        value: duration,
                        url: name,
                        navigationType: 'soft-navigation',
                        navigationId: navigationId,
                        startTime: startTime,
                    };
                    _this.onReport(softNavMetric);
                });
            });
            this.observers.push(softNavObserver);
            observeIfSupported(softNavObserver, PerformanceTypes.SoftNavigation);
        }
        else {
            console.warn('Coralogix Browser SDK - browser doesnt support soft navigation events');
        }
    };
    CoralogixWebVitalsInstrumentation.prototype.registerToCalculatedWebVitalMetrics = function () {
        if (this.metrics.lt) {
            if (supportedPerformanceTypes.has(PerformanceTypes.Navigation)) {
                this.calculateLoadingPageTime();
            }
            else {
                console.warn('Coralogix Browser SDK - browser doesnt support navigation events');
            }
        }
    };
    CoralogixWebVitalsInstrumentation.prototype.registerToTBTMetric = function () {
        if (this.metrics.tbt) {
            if (supportedPerformanceTypes.has(PerformanceTypes.LongTask)) {
                this.calculateAllTotalBlockingTime();
            }
            else {
                console.warn('Coralogix Browser SDK - browser doesnt support TBT events');
            }
        }
    };
    CoralogixWebVitalsInstrumentation.prototype.calculateLoadingPageTime = function () {
        var _this = this;
        var loadingPageObserver = new PerformanceObserver(function (list) {
            var loadTimeMetric;
            list.getEntries().forEach(function (entry) {
                var _a = entry, duration = _a.duration, name = _a.name, type = _a.type;
                loadTimeMetric = __assign(__assign({}, deepClone(entry)), { name: 'LT', id: generateWebVitalUniqueID(), value: duration, url: name, navigationType: type });
                if (duration) {
                    _this.onReport(loadTimeMetric);
                }
            });
        });
        this.observers.push(loadingPageObserver);
        observeIfSupported(loadingPageObserver, PerformanceTypes.Navigation);
    };
    CoralogixWebVitalsInstrumentation.prototype.calculateAllTotalBlockingTime = function () {
        var _this = this;
        var tbtMetric;
        var totalBLockingTimeObserver = new PerformanceObserver(function (list) {
            var _a;
            var totalBlockingTime = calculateTotalBlockingTime(list.getEntries());
            tbtMetric = {
                name: 'TBT',
                id: generateWebVitalUniqueID(),
                rating: rateForTBTMetricValue(totalBlockingTime),
                value: ((_a = tbtMetric === null || tbtMetric === void 0 ? void 0 : tbtMetric.value) !== null && _a !== void 0 ? _a : 0) + totalBlockingTime,
            };
        });
        this.observers.push(totalBLockingTimeObserver);
        observeIfSupported(totalBLockingTimeObserver, PerformanceTypes.LongTask);
        H.getFirstConsistentlyInteractive().then(function (tti) {
            var _a, _b;
            if (tti) {
                _this.onReport(tbtMetric);
                _this.onReport({
                    name: 'TTI',
                    value: tti,
                    id: generateWebVitalUniqueID(),
                });
                totalBLockingTimeObserver.disconnect();
                (_b = (_a = CxGlobal.__tti) === null || _a === void 0 ? void 0 : _a.o) === null || _b === void 0 ? void 0 : _b.disconnect();
            }
        });
    };
    CoralogixWebVitalsInstrumentation.prototype.enable = function () { };
    CoralogixWebVitalsInstrumentation.prototype.disable = function () {
        this.observers.forEach(function (observer) { return observer.disconnect(); });
    };
    CoralogixWebVitalsInstrumentation.prototype.init = function () { };
    return CoralogixWebVitalsInstrumentation;
}(InstrumentationBase));

var CoralogixLongTaskInstrumentation = (function (_super) {
    __extends(CoralogixLongTaskInstrumentation, _super);
    function CoralogixLongTaskInstrumentation(config) {
        var _this = _super.call(this, LONG_TASKS_INSTRUMENTATION_NAME, LONG_TASK_INSTRUMENTATION_VERSION, config) || this;
        if (supportedPerformanceTypes.has(PerformanceTypes.LongTask)) {
            _this.registerToLongTasks();
        }
        else {
            console.warn('Coralogix Browser SDK - browser doesnt support longtask events');
        }
        return _this;
    }
    CoralogixLongTaskInstrumentation.prototype.registerToLongTasks = function () {
        var _this = this;
        this.longTaskObserver = new PerformanceObserver(function (list) {
            list
                .getEntries()
                .forEach(function (entry) {
                return _this.createSpanFromLongTask(entry);
            });
        });
        observeIfSupported(this.longTaskObserver, PerformanceTypes.LongTask);
    };
    CoralogixLongTaskInstrumentation.prototype.createSpanFromLongTask = function (entry) {
        var longTaskSpan = this.tracer.startSpan(CoralogixEventType.LONG_TASK);
        var longTaskContext = __assign(__assign({}, deepClone(entry)), { id: generateUUID() });
        if (entry['toJSON']) {
            delete longTaskContext['toJSON'];
        }
        if (entry['attribution']) {
            delete longTaskContext['attribution'];
        }
        longTaskSpan.setAttribute(CoralogixAttributes.EVENT_TYPE, CoralogixEventType.LONG_TASK);
        longTaskSpan.setAttribute(CoralogixEventType.LONG_TASK, JSON.stringify(longTaskContext));
        longTaskSpan.end();
    };
    CoralogixLongTaskInstrumentation.prototype.enable = function () { };
    CoralogixLongTaskInstrumentation.prototype.disable = function () {
        var _a;
        (_a = this.longTaskObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
    };
    CoralogixLongTaskInstrumentation.prototype.init = function () { };
    return CoralogixLongTaskInstrumentation;
}(InstrumentationBase));

function resourceInitiatorTypeIs(value, types) {
    return types.some(function (type) { return type === value; });
}

var ResourceInitiatorTypes;
(function (ResourceInitiatorTypes) {
    ResourceInitiatorTypes["Media"] = "media";
    ResourceInitiatorTypes["Script"] = "script";
    ResourceInitiatorTypes["Css"] = "css";
    ResourceInitiatorTypes["Beacon"] = "beacon";
    ResourceInitiatorTypes["Img"] = "img";
    ResourceInitiatorTypes["Font"] = "font";
    ResourceInitiatorTypes["Doc"] = "doc";
    ResourceInitiatorTypes["Xhr"] = "xmlhttprequest";
    ResourceInitiatorTypes["Fetch"] = "fetch";
    ResourceInitiatorTypes["Iframe"] = "iframe";
    ResourceInitiatorTypes["Video"] = "video";
    ResourceInitiatorTypes["Audio"] = "audio";
    ResourceInitiatorTypes["Track"] = "track";
    ResourceInitiatorTypes["Other"] = "other";
})(ResourceInitiatorTypes || (ResourceInitiatorTypes = {}));

function isPatternMatch(str, arr, warnKey) {
    return arr.some(function (item) {
        if (item instanceof RegExp) {
            return item.test(str);
        }
        else if (typeof item === 'string') {
            item = item.toLowerCase();
            return item.includes(str.toLowerCase());
        }
        if (warnKey) {
            console.warn("Coralogix Browser SDK - ".concat(warnKey, " must be from type string | Regex"));
        }
        return false;
    });
}

var CoralogixResourcesInstrumentation = (function (_super) {
    __extends(CoralogixResourcesInstrumentation, _super);
    function CoralogixResourcesInstrumentation(config) {
        var _a;
        var _this = _super.call(this, RESOURCES_INSTRUMENTATION_NAME, RESOURCES_INSTRUMENTATION_VERSION, config) || this;
        _this.ignoreUrls = (_a = getSdkConfig()) === null || _a === void 0 ? void 0 : _a.ignoreUrls;
        if (supportedPerformanceTypes.has(PerformanceTypes.Resource)) {
            _this.registerToResources();
        }
        else {
            console.warn('Coralogix Browser SDK - browser doesnt support resource events');
        }
        return _this;
    }
    CoralogixResourcesInstrumentation.prototype.registerToResources = function () {
        var _this = this;
        this.resourcesObserver = new PerformanceObserver(function (list) {
            list.getEntries().forEach(function (entry) {
                if (_this.isProcessableResourceEntry(entry)) {
                    var mappedEntry = _this.mapResource(entry);
                    _this.createSpanFromResource(mappedEntry);
                }
            });
        });
        observeIfSupported(this.resourcesObserver, PerformanceTypes.Resource);
    };
    CoralogixResourcesInstrumentation.prototype.isProcessableResourceEntry = function (entry) {
        var isAcceptedResourceType = ![
            ResourceInitiatorTypes.Xhr,
            ResourceInitiatorTypes.Fetch,
        ].includes(entry
            .initiatorType);
        var isNotIgnoredResource = !this.isResourceIgnored(entry.name);
        return isAcceptedResourceType && isNotIgnoredResource;
    };
    CoralogixResourcesInstrumentation.prototype.isResourceIgnored = function (resourceName) {
        var _a;
        if (!resourceName || !((_a = this.ignoreUrls) === null || _a === void 0 ? void 0 : _a.length)) {
            return false;
        }
        return isPatternMatch(resourceName, this.ignoreUrls, 'ignoreUrls');
    };
    CoralogixResourcesInstrumentation.prototype.mapResource = function (resource) {
        var resourceName = resource.name;
        var initiatorType = resource.initiatorType;
        switch (true) {
            case resourceInitiatorTypeIs(initiatorType, [
                ResourceInitiatorTypes.Beacon,
            ]) || valueEndWithOrInclude(resourceName, OTHER_EXTENSIONS):
                initiatorType = ResourceInitiatorTypes.Other;
                break;
            case initiatorType !== ResourceInitiatorTypes.Css &&
                valueEndWithOrInclude(resourceName, CSS_EXTENSIONS):
                initiatorType = ResourceInitiatorTypes.Css;
                break;
            case initiatorType !== ResourceInitiatorTypes.Script &&
                valueEndWithOrInclude(resourceName, SCRIPT_EXTENSIONS):
                initiatorType = ResourceInitiatorTypes.Script;
                break;
            case initiatorType !== ResourceInitiatorTypes.Img &&
                valueEndWithOrInclude(resourceName, IMG_EXTENSIONS):
                initiatorType = ResourceInitiatorTypes.Img;
                break;
            case resourceName.includes(FONTS_URL) ||
                valueEndWithOrInclude(resourceName, FONT_EXTENSIONS):
                initiatorType = ResourceInitiatorTypes.Font;
                break;
            case initiatorType !== ResourceInitiatorTypes.Iframe &&
                valueEndWithOrInclude(resourceName, DOC_EXTENSIONS):
                initiatorType = ResourceInitiatorTypes.Doc;
                break;
            case resourceInitiatorTypeIs(initiatorType, [
                ResourceInitiatorTypes.Video,
                ResourceInitiatorTypes.Audio,
                ResourceInitiatorTypes.Track,
            ]) || valueEndWithOrInclude(resourceName, MEDIA_EXTENSIONS):
                initiatorType = ResourceInitiatorTypes.Media;
                break;
        }
        return __assign(__assign({}, deepClone(resource)), { initiatorType: initiatorType });
    };
    CoralogixResourcesInstrumentation.prototype.createSpanFromResource = function (entry) {
        var _a;
        var resourceSpan = this.tracer.startSpan(RESOURCES_INSTRUMENTATION_NAME);
        resourceSpan.setAttribute(CoralogixAttributes.EVENT_TYPE, CoralogixEventType.RESOURCES);
        var newEntry = __assign(__assign({}, deepClone(entry)), { name: (_a = entry.name) === null || _a === void 0 ? void 0 : _a.slice(0, MAX_CHARACTERS) });
        resourceSpan.setAttribute(RESOURCES_INSTRUMENTATION_NAME, JSON.stringify(newEntry));
        resourceSpan.end();
    };
    CoralogixResourcesInstrumentation.prototype.enable = function () { };
    CoralogixResourcesInstrumentation.prototype.disable = function () {
        var _a;
        (_a = this.resourcesObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
    };
    CoralogixResourcesInstrumentation.prototype.init = function () { };
    return CoralogixResourcesInstrumentation;
}(InstrumentationBase));

function getZoneJsOriginalDom(target, name) {
    var browserWindow = getCxGlobal();
    var original;
    if (browserWindow.Zone &&
        typeof browserWindow.Zone.__symbol__ === 'function') {
        original = target[browserWindow.Zone.__symbol__(name)];
    }
    if (!original) {
        original = target[name];
    }
    return original;
}

function addEventListeners(eventTarget, eventNames, listener, _a) {
    var _b = _a === void 0 ? {} : _a, once = _b.once, capture = _b.capture, passive = _b.passive;
    var resolvedListener = once
        ? function (event) {
            stop();
            listener(event);
        }
        : listener;
    var options = passive ? { capture: capture, passive: passive } : capture;
    var addListener = getZoneJsOriginalDom(eventTarget, 'addEventListener');
    eventNames.forEach(function (eventName) {
        return addListener.call(eventTarget, eventName, resolvedListener, options);
    });
    function stop() {
        var remove = getZoneJsOriginalDom(eventTarget, 'removeEventListener');
        eventNames.forEach(function (eventName) {
            return remove.call(eventTarget, eventName, resolvedListener, options);
        });
    }
    return {
        stop: stop,
    };
}

var PARENT_SEARCH_DEPTH = 5;
var CLICKABLE_ELEMENTS = ['BUTTON', 'LABEL', 'A', 'INPUT', 'OPTION'];
function extractMaskedOrTextContent(element, config) {
    if (!element)
        return '';
    var clickableElement = findClickableElement(element, PARENT_SEARCH_DEPTH);
    if (!clickableElement)
        return '';
    return getMaskedOrTextualContent(clickableElement, config);
}
function findClickableElement(element, maxDepth) {
    if (!element || maxDepth <= 0) {
        return null;
    }
    if (CLICKABLE_ELEMENTS.includes(element.nodeName))
        return element;
    return findClickableElement(element.parentElement, maxDepth - 1);
}
function getMaskedOrTextualContent(element, config) {
    if (shouldMaskElement(element, config.maskInputTypes, config.maskClass) ||
        isParentMasked(element, config.maskClass, PARENT_SEARCH_DEPTH)) {
        return MASKED_TEXT;
    }
    var maskedChild = findMaskedChildren(element, config);
    if (maskedChild) {
        return MASKED_TEXT;
    }
    return getTextualContent(element);
}
function isParentMasked(element, maskClass, maxDepth) {
    if (maxDepth === void 0) { maxDepth = PARENT_SEARCH_DEPTH; }
    if (!element || maxDepth <= 0) {
        return false;
    }
    if (isMaskedByClass(element.className, maskClass)) {
        return true;
    }
    return isParentMasked(element.parentElement, maskClass, maxDepth - 1);
}
function findMaskedChildren(element, config) {
    if (!element.children)
        return null;
    return Array.from(element.children).find(function (child) {
        return shouldMaskElement(child, config.maskInputTypes, config.maskClass);
    });
}
function shouldMaskElement(element, maskInputTypes, maskClass) {
    if (!element)
        return false;
    return (isMaskedInput(element, maskInputTypes) ||
        isMaskedByClass(element.className, maskClass));
}
function isMaskedInput(element, maskInputTypes) {
    if (element instanceof HTMLInputElement) {
        var inputType = element.type || 'text';
        return maskInputTypes.includes(inputType);
    }
    return false;
}
function isMaskedByClass(className, maskClass) {
    if (maskClass instanceof RegExp) {
        return maskClass.test(className);
    }
    if (typeof className !== 'string') {
        return false;
    }
    return className.includes(maskClass);
}
function getTextualContent(element) {
    if (element instanceof HTMLInputElement) {
        return element.value || '';
    }
    return 'innerText' in element ? element.innerText : '';
}

var _a$3;
var USER_INTERACTION_INSTRUMENTATION_NAME = 'interactions';
var USER_INTERACTION_INSTRUMENTATION_VERSION = '1';
var DEFAULT_INSTRUMENTED_EVENTS = (_a$3 = {},
    _a$3["click"] = true,
    _a$3);
var CoralogixUserInteractionInstrumentation = (function (_super) {
    __extends(CoralogixUserInteractionInstrumentation, _super);
    function CoralogixUserInteractionInstrumentation(config) {
        var _this = _super.call(this, USER_INTERACTION_INSTRUMENTATION_NAME, USER_INTERACTION_INSTRUMENTATION_VERSION, config) || this;
        _this.handler = function (_) { };
        _this.stop = function () { };
        return _this;
    }
    CoralogixUserInteractionInstrumentation.prototype.enable = function () {
        var _this = this;
        var _a;
        this.handler = function (e) {
            var span;
            if (shouldAttachSpanToGlobalSpan(CoralogixEventType.USER_INTERACTION)) {
                span = attachChildSpanToGlobalSpan(CoralogixEventType.USER_INTERACTION);
            }
            else {
                span = _this.tracer.startSpan(CoralogixEventType.USER_INTERACTION);
            }
            var eventTarget = e.target;
            var config = getSdkConfig();
            var id = eventTarget.id, type = eventTarget.type, className = eventTarget.className, nodeName = eventTarget.nodeName;
            var elementText = extractMaskedOrTextContent(eventTarget, config);
            span.setAttribute(CoralogixAttributes.EVENT_TYPE, CoralogixEventType.USER_INTERACTION);
            span.setAttribute(CoralogixAttributes.INTERACTION_EVENT_NAME, e.type);
            if (eventTarget && eventTarget instanceof HTMLElement) {
                [
                    [CoralogixAttributes.ELEMENT_ID, id],
                    [CoralogixAttributes.ELEMENT_CLASSES, className],
                    [CoralogixAttributes.TARGET_ELEMENT_TYPE, type],
                    [
                        CoralogixAttributes.ELEMENT_INNER_TEXT,
                        elementText === null || elementText === void 0 ? void 0 : elementText.slice(0, MAX_INTERACTION_ELEMENT_SIZE),
                    ],
                    [CoralogixAttributes.TARGET_ELEMENT, nodeName],
                ].forEach(function (_a) {
                    var _b = __read$1(_a, 2), key = _b[0], value = _b[1];
                    span.setAttribute(key, value);
                });
            }
            span.end();
        };
        var eventsMap = __assign(__assign({}, DEFAULT_INSTRUMENTED_EVENTS), (_a = this._config) === null || _a === void 0 ? void 0 : _a['events']);
        var eventNames = Object.entries(eventsMap)
            .filter(function (_a) {
            var _b = __read$1(_a, 2); _b[0]; var enabled = _b[1];
            return enabled;
        })
            .map(function (_a) {
            var _b = __read$1(_a, 1), eventName = _b[0];
            return eventName;
        });
        this.stop = addEventListeners(document, eventNames, this.handler, {
            capture: true,
            passive: true,
        }).stop;
    };
    CoralogixUserInteractionInstrumentation.prototype.disable = function () {
        this.stop();
    };
    CoralogixUserInteractionInstrumentation.prototype.init = function () { };
    return CoralogixUserInteractionInstrumentation;
}(InstrumentationBase));

var MEMORY_USAGE_INSTRUMENTATION_NAME = 'memory_usage';
var MEMORY_USAGE_INSTRUMENTATION_VERSION = '1.0.0';
var MEMORY_USAGE_DEFAULT_INTERVAL = 300000;

function isMeasureUserAgentSpecificMemoryAllowed() {
    if (!CxGlobal.crossOriginIsolated) {
        console.debug('Coralogix Browser SDK - measureUserAgentSpecificMemory() is only available in cross-origin-isolated pages.');
        return false;
    }
    if (!performance.measureUserAgentSpecificMemory) {
        console.debug('Coralogix Browser SDK - measureUserAgentSpecificMemory() is not available in this browser');
        return false;
    }
    return true;
}
function mapMemoryUsageEntries(memoryUsage) {
    var e_1, _a;
    var uuid = generateUUID();
    var parentEntry = {
        uuid: uuid,
        isParent: true,
        bytes: memoryUsage.bytes,
    };
    var breakdownEntries = [];
    try {
        for (var _b = __values$1(memoryUsage.breakdown), _c = _b.next(); !_c.done; _c = _b.next()) {
            var entry = _c.value;
            if (entry.bytes > 0) {
                breakdownEntries.push({
                    uuid: uuid,
                    bytes: entry.bytes,
                    attribution: entry.attribution,
                    types: entry.types.sort().join(', '),
                });
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return __spreadArray([parentEntry], __read$1(breakdownEntries), false);
}

var CoralogixMemoryUsageInstrumentation = (function (_super) {
    __extends(CoralogixMemoryUsageInstrumentation, _super);
    function CoralogixMemoryUsageInstrumentation(config) {
        var _this = _super.call(this, MEMORY_USAGE_INSTRUMENTATION_NAME, MEMORY_USAGE_INSTRUMENTATION_VERSION, config) || this;
        _this.memoryUsageInterval = getSdkConfig().memoryUsageConfig.interval;
        return _this;
    }
    CoralogixMemoryUsageInstrumentation.prototype.disable = function () {
        this.clearMeasurementsInterval();
    };
    CoralogixMemoryUsageInstrumentation.prototype.enable = function () {
        this.startTrackingMemoryUsage();
    };
    CoralogixMemoryUsageInstrumentation.prototype.init = function () { };
    CoralogixMemoryUsageInstrumentation.prototype.startTrackingMemoryUsage = function () {
        var _this = this;
        this.clearMeasurementsInterval();
        this.createMemorySample().then(function () { return _this.runMemoryMeasurements(); });
    };
    CoralogixMemoryUsageInstrumentation.prototype.createMemorySample = function () {
        return __awaiter(this, void 0, void 0, function () {
            var memoryUsage, memoryUsageEntries, memoryUsageEntries_1, memoryUsageEntries_1_1, entry, memoryUsageSpan, error_1;
            var e_1, _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 2, , 3]);
                        return [4, performance.measureUserAgentSpecificMemory()];
                    case 1:
                        memoryUsage = _b.sent();
                        memoryUsageEntries = mapMemoryUsageEntries(memoryUsage);
                        try {
                            for (memoryUsageEntries_1 = __values$1(memoryUsageEntries), memoryUsageEntries_1_1 = memoryUsageEntries_1.next(); !memoryUsageEntries_1_1.done; memoryUsageEntries_1_1 = memoryUsageEntries_1.next()) {
                                entry = memoryUsageEntries_1_1.value;
                                memoryUsageSpan = this.tracer.startSpan(MEMORY_USAGE_INSTRUMENTATION_NAME);
                                memoryUsageSpan[CoralogixAttributes.EVENT_TYPE] =
                                    CoralogixEventType.MEMORY_USAGE;
                                memoryUsageSpan[CoralogixAttributes.MEMORY_USAGE_CONTEXT] = entry;
                                memoryUsageSpan.end();
                            }
                        }
                        catch (e_1_1) { e_1 = { error: e_1_1 }; }
                        finally {
                            try {
                                if (memoryUsageEntries_1_1 && !memoryUsageEntries_1_1.done && (_a = memoryUsageEntries_1.return)) _a.call(memoryUsageEntries_1);
                            }
                            finally { if (e_1) throw e_1.error; }
                        }
                        return [3, 3];
                    case 2:
                        error_1 = _b.sent();
                        console.warn('Memory usage measurement failed', error_1);
                        this.clearMeasurementsInterval();
                        return [3, 3];
                    case 3: return [2];
                }
            });
        });
    };
    CoralogixMemoryUsageInstrumentation.prototype.runMemoryMeasurements = function () {
        var _this = this;
        this.timeoutId = setTimeout(function () { return _this.measureMemoryUsage(); }, this.memoryUsageInterval);
    };
    CoralogixMemoryUsageInstrumentation.prototype.measureMemoryUsage = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4, this.createMemorySample()];
                    case 1:
                        _a.sent();
                        this.runMemoryMeasurements();
                        return [2];
                }
            });
        });
    };
    CoralogixMemoryUsageInstrumentation.prototype.clearMeasurementsInterval = function () {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }
    };
    return CoralogixMemoryUsageInstrumentation;
}(InstrumentationBase));

var SCREENSHOT_INSTRUMENTATION_NAME = 'screenshot';
var SCREENSHOT_INSTRUMENTATION_VERSION = '1.0.0';

var CoralogixScreenshotInstrumentation = (function (_super) {
    __extends(CoralogixScreenshotInstrumentation, _super);
    function CoralogixScreenshotInstrumentation(config) {
        return _super.call(this, SCREENSHOT_INSTRUMENTATION_NAME, SCREENSHOT_INSTRUMENTATION_VERSION, config) || this;
    }
    CoralogixScreenshotInstrumentation.prototype.init = function () { };
    CoralogixScreenshotInstrumentation.prototype.disable = function () { };
    CoralogixScreenshotInstrumentation.prototype.enable = function () { };
    CoralogixScreenshotInstrumentation.prototype.screenshot = function (id, description) {
        if (!getSessionRecorder()) {
            return;
        }
        var span = this.tracer.startSpan(CoralogixEventType.SCREENSHOT);
        span.setAttribute(CoralogixAttributes.EVENT_TYPE, CoralogixEventType.SCREENSHOT);
        span.setAttribute(CoralogixAttributes.SEVERITY, CoralogixLogSeverity.Info);
        span[CoralogixAttributes.SCREENSHOT_CONTEXT] = {
            id: id,
            description: description,
        };
        span.end();
    };
    return CoralogixScreenshotInstrumentation;
}(InstrumentationBase));

var TimeMeasurementTracker = (function () {
    function TimeMeasurementTracker() {
        this.measurements = new Map();
    }
    TimeMeasurementTracker.prototype.startMeasurement = function (key, labels) {
        this.checkAndResetIfSessionExpired();
        if (this.measurements.has(key) || !key.trim()) {
            this.logDebug("Invalid or duplicate key: \"".concat(key, "\"."));
            return;
        }
        this.measurements.set(key, { startTime: Date.now(), labels: labels });
    };
    TimeMeasurementTracker.prototype.getMeasurement = function (key) {
        this.checkAndResetIfSessionExpired();
        if (!this.measurements.has(key) || !key.trim()) {
            this.logDebug("Invalid or duplicate key: \"".concat(key, "\"."));
            return undefined;
        }
        var _a = this.measurements.get(key), startTime = _a.startTime, labels = _a.labels;
        this.measurements.delete(key);
        return {
            duration: Date.now() - startTime,
            labels: labels,
        };
    };
    TimeMeasurementTracker.prototype.checkAndResetIfSessionExpired = function () {
        if (getSessionManager().isIdleActive) {
            this.measurements.clear();
        }
    };
    TimeMeasurementTracker.prototype.logDebug = function (message) {
        if (getSdkConfig().debug) {
            console.debug("Coralogix Browser SDK - ".concat(message));
        }
    };
    return TimeMeasurementTracker;
}());

var CoralogixCustomMeasurementInstrumentation = (function (_super) {
    __extends(CoralogixCustomMeasurementInstrumentation, _super);
    function CoralogixCustomMeasurementInstrumentation(config) {
        return _super.call(this, CUSTOM_MEASUREMENT_INSTRUMENTATION_NAME, CUSTOM_MEASUREMENT_INSTRUMENTATION_VERSION, config) || this;
    }
    CoralogixCustomMeasurementInstrumentation.prototype.init = function () { };
    CoralogixCustomMeasurementInstrumentation.prototype.disable = function () { };
    CoralogixCustomMeasurementInstrumentation.prototype.enable = function () { };
    CoralogixCustomMeasurementInstrumentation.prototype.createCustomMeasurement = function (name, value, labels) {
        var customMeasurementSpan = this.tracer.startSpan(CUSTOM_MEASUREMENT_INSTRUMENTATION_NAME);
        customMeasurementSpan[CoralogixAttributes.EVENT_TYPE] =
            CoralogixEventType.CUSTOM_MEASUREMENT;
        customMeasurementSpan[CoralogixAttributes.CUSTOM_MEASUREMENT_CONTEXT] = {
            name: name,
            value: value,
        };
        if (Object.keys(labels || {}).length > 0) {
            customMeasurementSpan.setAttribute(CoralogixAttributes.CUSTOM_LABELS, JSON.stringify(labels));
        }
        customMeasurementSpan.end();
    };
    return CoralogixCustomMeasurementInstrumentation;
}(InstrumentationBase));

var INTERNAL_INSTRUMENTATION_VERSION = '1';
var CoralogixInternalInstrumentation = (function (_super) {
    __extends(CoralogixInternalInstrumentation, _super);
    function CoralogixInternalInstrumentation(config) {
        return _super.call(this, CoralogixEventType.INTERNAL, INTERNAL_INSTRUMENTATION_VERSION, config) || this;
    }
    CoralogixInternalInstrumentation.prototype.init = function () { };
    CoralogixInternalInstrumentation.prototype.disable = function () { };
    CoralogixInternalInstrumentation.prototype.enable = function () { };
    CoralogixInternalInstrumentation.prototype.reportInternalEvent = function (event, message) {
        if (message === void 0) { message = ''; }
        var span = this.tracer.startSpan(CoralogixEventType.INTERNAL);
        var data;
        span.setAttribute(CoralogixAttributes.EVENT_TYPE, CoralogixEventType.INTERNAL);
        span.setAttribute(CoralogixAttributes.SEVERITY, CoralogixLogSeverity.Info);
        switch (event) {
            case 'init': {
                var config = omit(getSdkConfig(), 'public_key', 'coralogixDomain');
                data = this.stringifyConfig(config);
                break;
            }
            case 'recording-start':
                break;
            case 'recording-stop':
                data = message;
                break;
        }
        span.setAttribute(CoralogixAttributes.INTERNAL, JSON.stringify({
            event: event,
            data: data,
        }));
        span.end();
    };
    CoralogixInternalInstrumentation.prototype.stringifyConfig = function (config) {
        var replacer = function (key, value) {
            if (value instanceof RegExp) {
                return value.toString();
            }
            if (typeof value === 'function') {
                return value.toString().slice(0, 8) + '...';
            }
            return value;
        };
        return JSON.stringify(config, replacer, 2);
    };
    return CoralogixInternalInstrumentation;
}(InstrumentationBase));

var CoralogixDOMInstrumentation = (function (_super) {
    __extends(CoralogixDOMInstrumentation, _super);
    function CoralogixDOMInstrumentation(config) {
        var _this = _super.call(this, DOM_INSTRUMENTATION_NAME, DOM_INSTRUMENTATION_VERSION, config) || this;
        _this.visibilityChangeHandler = function () {
            if (document.hidden) {
                var domSpan = _this.tracer.startSpan(DOM_INSTRUMENTATION_NAME);
                domSpan.setAttribute(CoralogixAttributes.EVENT_TYPE, CoralogixEventType.DOM);
                domSpan.end();
            }
        };
        return _this;
    }
    CoralogixDOMInstrumentation.prototype.registerToDOMEvents = function () {
        document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    };
    CoralogixDOMInstrumentation.prototype.unregisterFromDOMEvents = function () {
        document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    };
    CoralogixDOMInstrumentation.prototype.init = function () { };
    CoralogixDOMInstrumentation.prototype.disable = function () {
        this.unregisterFromDOMEvents();
    };
    CoralogixDOMInstrumentation.prototype.enable = function () {
        var _this = this;
        setTimeout(function () {
            _this.registerToDOMEvents();
        });
    };
    return CoralogixDOMInstrumentation;
}(InstrumentationBase));

var ERROR_TYPES = {
    RUNTIME: 'web-worker-runtime-error',
    MESSAGE_ERROR: 'web-worker-messageerror',
};
var ERROR_MESSAGES = {
    UNKNOWN_RUNTIME: 'Unknown worker runtime error',
    DESERIALIZATION_FAILED: 'Failed to deserialize message from worker',
};
var WORKER_EVENTS = {
    ERROR: 'error',
    MESSAGE_ERROR: 'messageerror',
};

var CoralogixWorkerInstrumentation = (function (_super) {
    __extends(CoralogixWorkerInstrumentation, _super);
    function CoralogixWorkerInstrumentation(config, errorInstrumentation, coralogixRum) {
        var _this = _super.call(this, WORKER_INSTRUMENTATION, WORKER_INSTRUMENTATION_VERSION, config) || this;
        _this.errorInstrumentation = errorInstrumentation;
        _this.coralogixRum = coralogixRum;
        _this.init();
        return _this;
    }
    CoralogixWorkerInstrumentation.prototype.disable = function () { };
    CoralogixWorkerInstrumentation.prototype.enable = function () { };
    CoralogixWorkerInstrumentation.prototype.init = function () {
        var globalObj = CxGlobal;
        var OriginalWorker = globalObj.Worker;
        globalObj.Worker = this.createCxWorker(OriginalWorker);
    };
    CoralogixWorkerInstrumentation.prototype.createCxWorker = function (OriginalWorker) {
        var self = this;
        function CxWorker(scriptURL, options) {
            var worker = new OriginalWorker(scriptURL, options);
            worker.CoralogixRum = self.coralogixRum;
            self.attachWorkerErrorHandlers(worker, scriptURL);
            return worker;
        }
        CxWorker.prototype = OriginalWorker.prototype;
        Object.defineProperties(CxWorker, Object.getOwnPropertyDescriptors(OriginalWorker));
        return CxWorker;
    };
    CoralogixWorkerInstrumentation.prototype.attachWorkerErrorHandlers = function (worker, scriptURL) {
        var _this = this;
        var source = scriptURL.toString();
        worker.addEventListener(WORKER_EVENTS.ERROR, function (event) {
            var message = event.message || ERROR_MESSAGES.UNKNOWN_RUNTIME;
            _this.reportWorkerError(message, {
                scriptURL: source,
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                originalError: event.error || null,
            }, { type: ERROR_TYPES.RUNTIME });
        });
        worker.addEventListener(WORKER_EVENTS.MESSAGE_ERROR, function (event) {
            _this.reportWorkerError(ERROR_MESSAGES.DESERIALIZATION_FAILED, {
                scriptURL: source,
                event: event,
            }, { type: ERROR_TYPES.MESSAGE_ERROR });
        });
    };
    CoralogixWorkerInstrumentation.prototype.reportWorkerError = function (message, customData, labels) {
        var _a;
        (_a = this.errorInstrumentation) === null || _a === void 0 ? void 0 : _a.reportError(ErrorSource.WEB_WORKER, new Error(message), customData, labels);
    };
    return CoralogixWorkerInstrumentation;
}(InstrumentationBase));

var CORALOGIX_LOGS_URL_SUFFIX = '/browser/v1beta/logs';
var CORALOGIX_RECORDING_URL_SUFFIX = '/sessionrecording';
var MAX_EXPORT_BATCH_SIZE = 50;
var MAX_INTERACTION_ELEMENT_SIZE = 1024;
var MAX_CHARACTERS = 1024;
var SCHEDULE_DELAY_MILLIS = 2 * millisecondsPerSecond;
var REQUIRED_CONFIG_KEYS = [
    'application',
    'version',
    'coralogixDomain',
];
var SDK_CONFIG_KEY = 'sdkConfig';
var USER_AGENT_KEY = 'userAgent';
var ID_MASK_KEY = '{id}';
var UUID_REGEX = /[0-9a-fA-F-]{36}/g;
var NANOID_REGEX = /^[a-zA-Z0-9-_]{21}$/;
var PAGE_URL_LABEL_PROVIDERS_KEY = 'PAGE_URL_LABEL_PROVIDERS';
var NETWORK_URL_LABEL_PROVIDERS_KEY = 'NETWORK_URL_LABEL_PROVIDERS';
var RUM_INTERNAL_DATA_KEY = 'rumInternalData';
var MASKED_TEXT = '***';
var MASK_CLASS_DEFAULT = 'cx-mask';
var MASK_INPUT_TYPES_DEFAULT = [
    'password',
    'email',
    'tel',
];
var FINGER_PRINT_KEY = 'cx-fingerprint';
var idPatterns = [
    {
        pattern: new RegExp(UUID_REGEX),
        replacement: ID_MASK_KEY,
    },
    {
        pattern: new RegExp(NANOID_REGEX),
        replacement: ID_MASK_KEY,
    },
];
var defaultUrlBlueprinters = {
    pageUrlBlueprinters: [
        function (url) { return replacePatternsInUrl({ patterns: idPatterns, url: url }); },
    ],
};
var OPTIONS_DEFAULTS = {
    ignoreUrls: [
        new RegExp(CORALOGIX_LOGS_URL_SUFFIX),
        new RegExp(CORALOGIX_RECORDING_URL_SUFFIX),
    ],
    user_context: {
        user_id: '',
        user_name: '',
    },
    labels: {},
    urlBlueprinters: defaultUrlBlueprinters,
    collectIPData: true,
};
var INSTRUMENTATIONS = [
    {
        Instrument: CoralogixErrorInstrumentation,
        confKey: ERROR_INSTRUMENTATION_NAME,
        disable: false,
    },
    {
        Instrument: CoralogixFetchInstrumentation,
        confKey: FETCH_INSTRUMENTATION_NAME,
        disable: false,
    },
    {
        Instrument: CoralogixXhrInstrumentation,
        confKey: XHR_INSTRUMENTATION_NAME,
        disable: false,
    },
    {
        Instrument: CoralogixCustomLogInstrumentation,
        confKey: CUSTOM_INSTRUMENTATION_NAME,
        disable: false,
    },
    {
        Instrument: CoralogixUserInteractionInstrumentation,
        confKey: USER_INTERACTION_INSTRUMENTATION_NAME,
        disable: false,
    },
    {
        Instrument: CoralogixWebVitalsInstrumentation,
        confKey: WEB_VITALS_INSTRUMENTATION_NAME,
        disable: false,
    },
    {
        Instrument: CoralogixLongTaskInstrumentation,
        confKey: LONG_TASKS_INSTRUMENTATION_NAME,
        disable: false,
    },
    {
        Instrument: CoralogixResourcesInstrumentation,
        confKey: RESOURCES_INSTRUMENTATION_NAME,
        disable: false,
    },
    {
        Instrument: CoralogixInternalInstrumentation,
        confKey: CoralogixEventType.INTERNAL,
        disable: false,
    },
    {
        Instrument: CoralogixDOMInstrumentation,
        confKey: CoralogixEventType.DOM,
        disable: false,
    },
    {
        Instrument: CoralogixCustomMeasurementInstrumentation,
        confKey: CoralogixEventType.CUSTOM_MEASUREMENT,
        disable: false,
    },
    {
        Instrument: CoralogixMemoryUsageInstrumentation,
        confKey: CoralogixEventType.MEMORY_USAGE,
        disable: false,
    },
    {
        Instrument: CoralogixScreenshotInstrumentation,
        confKey: CoralogixEventType.SCREENSHOT,
        disable: false,
    },
    {
        Instrument: CoralogixWorkerInstrumentation,
        confKey: CoralogixEventType.WORKER,
        disable: false,
    },
];
var CoralogixAttributes = {
    EVENT_TYPE: 'event_type',
    SEVERITY: 'severity',
    APPLICATION_CONTEXT: 'application_context',
    USER_CONTEXT: 'user_context',
    CUSTOM_MEASUREMENT_CONTEXT: 'custom_measurement_context',
    MEMORY_USAGE_CONTEXT: 'memory_usage_context',
    SOURCE: 'source',
    ERROR: ErrorAttributes,
    LOG: 'cx_log',
    CUSTOM_LABELS: 'custom_labels',
    ENVIRONMENT: 'environment',
    INTERACTION_EVENT_NAME: 'interaction_event_name',
    ELEMENT_INNER_TEXT: 'element_inner_text',
    ELEMENT_ID: 'element_id',
    ELEMENT_CLASSES: 'element_classes',
    TARGET_ELEMENT: 'target_element',
    TARGET_ELEMENT_TYPE: 'target_element_type',
    TIMESTAMP: 'timestamp',
    INTERNAL: 'internal',
    PAGE_CONTEXT: 'page_context',
    IS_NAVIGATION_EVENT: 'is_navigation_event',
    SCREENSHOT_CONTEXT: 'screenshot_context',
};
var CoralogixDomainsApiUrlMap = {
    EU1: 'https://ingress.eu1.rum-ingress-coralogix.com',
    EU2: 'https://ingress.eu2.rum-ingress-coralogix.com',
    US1: 'https://ingress.us1.rum-ingress-coralogix.com',
    US2: 'https://ingress.us2.rum-ingress-coralogix.com',
    AP1: 'https://ingress.ap1.rum-ingress-coralogix.com',
    AP2: 'https://ingress.ap2.rum-ingress-coralogix.com',
    AP3: 'https://ingress.ap3.rum-ingress-coralogix.com',
    STAGING: 'https://ingress.staging.rum-ingress-coralogix.com',
};
var PROXY_CX_FORWARD_PARAMETER = 'cxforward';
var CX_MAPPED_SPAN = 'cx_mapped_span';
var BASE_PATH = '/';
var HASH_SEPARATOR = '#';
var PARAMS_SEPARATOR = '?';
var PROTOCOL_SEPARATOR = '://';

function saveInternalRumData(key, value) {
    if (!CxGlobal[RUM_INTERNAL_DATA_KEY]) {
        CxGlobal[RUM_INTERNAL_DATA_KEY] = {};
    }
    CxGlobal[RUM_INTERNAL_DATA_KEY][key] = value;
}
function getInternalRumData(key) {
    var _a;
    return (_a = CxGlobal[RUM_INTERNAL_DATA_KEY]) === null || _a === void 0 ? void 0 : _a[key];
}

var _a$2;
var supportedPerformanceTypes = !((_a$2 = CxGlobal === null || CxGlobal === void 0 ? void 0 : CxGlobal.PerformanceObserver) === null || _a$2 === void 0 ? void 0 : _a$2.supportedEntryTypes)
    ? new Set()
    : new Set(CxGlobal.PerformanceObserver.supportedEntryTypes);
function valueEndWithOrInclude(value, extensions) {
    return extensions.some(function (ex) { return value.endsWith(ex) || value.includes(ex); });
}
function getInstrumentationConfig(value, defaults, defaultDisable) {
    if (value === false) {
        return value;
    }
    if (value === undefined && defaultDisable) {
        return false;
    }
    if ((value === null || value === void 0 ? void 0 : value.enabled) === false) {
        return false;
    }
    return Object.assign({}, defaults, value);
}
var hrTimeToMilliseconds = function (_a) {
    var _b = __read$1(_a, 2), seconds = _b[0], nanoseconds = _b[1];
    return seconds * 1000 + Math.round(nanoseconds / 1e6);
};
var isNetworkError = function (status) {
    if (!status) {
        return false;
    }
    return status >= 400;
};
var getUrlFragments = function (url) {
    var _a, _b;
    if (!url) {
        return '';
    }
    var withoutProtocol = ((_a = url.split(PROTOCOL_SEPARATOR)) === null || _a === void 0 ? void 0 : _a[1]) || url;
    var startPath = (_b = withoutProtocol.split(BASE_PATH)) === null || _b === void 0 ? void 0 : _b[1];
    if (startPath === null || startPath === void 0 ? void 0 : startPath.startsWith(HASH_SEPARATOR)) {
        var fragments = withoutProtocol.split(HASH_SEPARATOR)[1];
        var cleanedFragments = fragments.split(PARAMS_SEPARATOR)[0];
        var pathFragments = cleanedFragments.split(BASE_PATH);
        if (pathFragments.length) {
            pathFragments.shift();
        }
        return pathFragments.join(BASE_PATH) || BASE_PATH;
    }
    else {
        try {
            if (url.startsWith('blob:')) {
                return url;
            }
            return new URL(url).pathname.substring(1) || BASE_PATH;
        }
        catch (err) {
            console.warn('Coralogix Browser SDK - Error parsing URL', err);
            return url;
        }
    }
};
function parseUserAgent(userAgent) {
    var e_1, _a, e_2, _b, e_3, _c;
    var _d, _e, _f;
    var browsers = {
        Edge: /Edge\/([\d.]+)|Edg\/([\d.]+)/,
        Chrome: /Chrome\/([\d.]+)/,
        Firefox: /Firefox\/([\d.]+)/,
        Safari: /Version\/([\d.]+).*Safari|Safari\/([\d.]+)/,
        Opera: /Opera\/([\d.]+)/,
        IE: /Trident.*rv:([\d.]+)/,
    };
    var operatingSystems = {
        Windows: /Windows NT ([\d.]+)/,
        MacOS: /Mac OS X ([\d._]+)/,
        webOS: /Web0S/,
        Linux: /Linux(?: ([\d.]+))?/,
        iOS: /CPU(?: iPhone)? OS (\d+([._]\d+)*)/,
        Android: /Android (\d+([._]\d+)*)/,
        Tizen: /Tizen ([\d.]+)/,
    };
    var devices = {
        Mobile: /Mobile|Android|iPhone|iPod|BlackBerry|IEMobile|Windows Phone|webOS|Opera Mini/,
        Tablet: /Tablet|iPad/,
        'Smart-TV': /SMART-TV|SmartTV/,
    };
    var browser = 'Unknown';
    var browserVersion = 'Unknown';
    var os = 'Unknown';
    var osVersion = 'Unknown';
    var device = 'Desktop';
    try {
        for (var _g = __values$1(Object.entries(browsers)), _h = _g.next(); !_h.done; _h = _g.next()) {
            var _j = __read$1(_h.value, 2), name_1 = _j[0], regex = _j[1];
            var match = userAgent.match(regex);
            if (match) {
                browser = name_1;
                browserVersion = ((_d = match.slice(1)) === null || _d === void 0 ? void 0 : _d.find(function (v) { return !!v; })) || 'Unknown';
                break;
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_h && !_h.done && (_a = _g.return)) _a.call(_g);
        }
        finally { if (e_1) throw e_1.error; }
    }
    try {
        for (var _k = __values$1(Object.entries(operatingSystems)), _l = _k.next(); !_l.done; _l = _k.next()) {
            var _m = __read$1(_l.value, 2), name_2 = _m[0], regex = _m[1];
            var match = userAgent.match(regex);
            if (match) {
                os = name_2;
                osVersion = ((_f = (_e = match[1]) === null || _e === void 0 ? void 0 : _e.split('_')) === null || _f === void 0 ? void 0 : _f.join('.')) || 'Unknown';
                break;
            }
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (_l && !_l.done && (_b = _k.return)) _b.call(_k);
        }
        finally { if (e_2) throw e_2.error; }
    }
    try {
        for (var _o = __values$1(Object.entries(devices)), _p = _o.next(); !_p.done; _p = _o.next()) {
            var _q = __read$1(_p.value, 2), name_3 = _q[0], regex = _q[1];
            var match = userAgent.match(regex);
            if (match) {
                device = name_3;
                break;
            }
        }
    }
    catch (e_3_1) { e_3 = { error: e_3_1 }; }
    finally {
        try {
            if (_p && !_p.done && (_c = _o.return)) _c.call(_o);
        }
        finally { if (e_3) throw e_3.error; }
    }
    return { browser: browser, browserVersion: browserVersion, os: os, osVersion: osVersion, device: device };
}
function calculateTotalBlockingTime(list) {
    return list.reduce(function (total, entry) {
        total += entry.duration - LONGTASK_DURATION;
        return total;
    }, 0);
}
function generateWebVitalUniqueID() {
    return "v3-".concat(getNowTime(), "-").concat(Math.floor(Math.random() * (9e12 - 1)) + 1e12);
}
function rateForTBTMetricValue(value) {
    switch (true) {
        case value > 600:
            return 'poor';
        case value > 200 && value <= 600:
            return 'needs-improvement';
        default:
            return 'good';
    }
}
function flattenAttributes(attributes) {
    var e_4, _a;
    var customLabels = JSON.parse(attributes[CoralogixAttributes.CUSTOM_LABELS]);
    var flatAttributes = omit(attributes, CoralogixAttributes.CUSTOM_LABELS);
    try {
        for (var _b = __values$1(Object.entries(customLabels)), _c = _b.next(); !_c.done; _c = _b.next()) {
            var _d = __read$1(_c.value, 2), key = _d[0], value = _d[1];
            flatAttributes[key] =
                typeof value !== 'string' ? JSON.stringify(value) : value;
        }
    }
    catch (e_4_1) { e_4 = { error: e_4_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_4) throw e_4.error; }
    }
    return flatAttributes;
}
var getCircularReplacer = function () {
    var seen = new WeakSet();
    return function (key, value) {
        if (typeof value === 'object' && value !== null) {
            if (seen.has(value)) {
                return;
            }
            seen.add(value);
        }
        return value;
    };
};
function replacePatternsInUrl(_a) {
    var e_5, _b;
    var patterns = _a.patterns, url = _a.url;
    var modifiedUrl = url;
    try {
        for (var patterns_1 = __values$1(patterns), patterns_1_1 = patterns_1.next(); !patterns_1_1.done; patterns_1_1 = patterns_1.next()) {
            var _c = patterns_1_1.value, pattern = _c.pattern, replacement = _c.replacement;
            modifiedUrl = modifiedUrl.replace(pattern, replacement);
        }
    }
    catch (e_5_1) { e_5 = { error: e_5_1 }; }
    finally {
        try {
            if (patterns_1_1 && !patterns_1_1.done && (_b = patterns_1.return)) _b.call(patterns_1);
        }
        finally { if (e_5) throw e_5.error; }
    }
    return modifiedUrl;
}
function applyUrlBluePrinters(_a) {
    var url = _a.url, blueprinters = _a.blueprinters;
    var blueprintedUrl = url;
    blueprinters === null || blueprinters === void 0 ? void 0 : blueprinters.forEach(function (urlBlueprinter) { return (blueprintedUrl = urlBlueprinter(blueprintedUrl)); });
    return blueprintedUrl;
}
function resolvePageContext(url) {
    var _a;
    var page_url_blueprint = applyUrlBluePrinters({
        url: url,
        blueprinters: (_a = getSdkConfig().urlBlueprinters) === null || _a === void 0 ? void 0 : _a.pageUrlBlueprinters,
    });
    var page_fragments = getUrlFragments(page_url_blueprint);
    return {
        page_url: url.slice(0, MAX_CHARACTERS),
        page_url_blueprint: page_url_blueprint,
        page_fragments: page_fragments,
        referrer: document.referrer,
    };
}
function deepClone(obj) {
    return JSON.parse(JSON.stringify(obj));
}
function partition(array, predicate) {
    var e_6, _a;
    var truthyValues = [];
    var falsyValues = [];
    try {
        for (var array_1 = __values$1(array), array_1_1 = array_1.next(); !array_1_1.done; array_1_1 = array_1.next()) {
            var element = array_1_1.value;
            if (predicate(element)) {
                truthyValues.push(element);
            }
            else {
                falsyValues.push(element);
            }
        }
    }
    catch (e_6_1) { e_6 = { error: e_6_1 }; }
    finally {
        try {
            if (array_1_1 && !array_1_1.done && (_a = array_1.return)) _a.call(array_1);
        }
        finally { if (e_6) throw e_6.error; }
    }
    return [truthyValues, falsyValues];
}

var CoralogixSpanAttributesProcessor = (function () {
    function CoralogixSpanAttributesProcessor(labels) {
        var _a;
        this.labels = (_a = {},
            _a[CoralogixAttributes.CUSTOM_LABELS] = '{}',
            _a);
        if (labels) {
            this.labels = labels;
        }
    }
    CoralogixSpanAttributesProcessor.prototype.setCustomLabels = function (labels) {
        this.labels[CoralogixAttributes.CUSTOM_LABELS] = JSON.stringify(labels !== null && labels !== void 0 ? labels : {});
    };
    CoralogixSpanAttributesProcessor.prototype.setInternalLabels = function (labels) {
        var _a;
        this.labels = __assign((_a = {}, _a[CoralogixAttributes.CUSTOM_LABELS] = this.labels[CoralogixAttributes.CUSTOM_LABELS], _a), (labels ? this.stringifyValues(labels) : {}));
    };
    CoralogixSpanAttributesProcessor.prototype.getLabels = function () {
        return JSON.parse(JSON.stringify(this.labels));
    };
    CoralogixSpanAttributesProcessor.prototype.getCustomLabels = function () {
        return JSON.parse(this.labels[CoralogixAttributes.CUSTOM_LABELS]);
    };
    CoralogixSpanAttributesProcessor.prototype.getInternalLabels = function () {
        var labels = __assign({}, this.labels);
        delete labels[CoralogixAttributes.CUSTOM_LABELS];
        return labels;
    };
    CoralogixSpanAttributesProcessor.prototype.getUserContext = function () {
        var userContext = this.labels[CoralogixAttributes.USER_CONTEXT];
        return userContext ? JSON.parse("".concat(userContext)) : undefined;
    };
    CoralogixSpanAttributesProcessor.prototype.forceFlush = function () {
        return Promise.resolve();
    };
    CoralogixSpanAttributesProcessor.prototype.onStart = function (span) {
        span.setAttribute(CoralogixAttributes.TIMESTAMP, getNowTime());
        span.setAttributes(this.labels);
    };
    CoralogixSpanAttributesProcessor.prototype.onEnd = function () { };
    CoralogixSpanAttributesProcessor.prototype.shutdown = function () {
        return Promise.resolve();
    };
    CoralogixSpanAttributesProcessor.prototype.stringifyValues = function (labels) {
        var resolvedLabels = {};
        for (var key in labels) {
            resolvedLabels[key] =
                typeof labels[key] === 'string'
                    ? labels[key]
                    : JSON.stringify(labels[key]);
        }
        return resolvedLabels;
    };
    return CoralogixSpanAttributesProcessor;
}());

var SUPPRESS_TRACING_KEY = createContextKey('OpenTelemetry SDK Context Key SUPPRESS_TRACING');
function isTracingSuppressed(context) {
  return context.getValue(SUPPRESS_TRACING_KEY) === true;
}

var BAGGAGE_KEY_PAIR_SEPARATOR = '=';
var BAGGAGE_PROPERTIES_SEPARATOR = ';';
var BAGGAGE_ITEMS_SEPARATOR = ',';
var BAGGAGE_HEADER = 'baggage';
var BAGGAGE_MAX_NAME_VALUE_PAIRS = 180;
var BAGGAGE_MAX_PER_NAME_VALUE_PAIRS = 4096;
var BAGGAGE_MAX_TOTAL_LENGTH = 8192;

var __read = undefined && undefined.__read || function (o, n) {
  var m = typeof Symbol === "function" && o[Symbol.iterator];
  if (!m) return o;
  var i = m.call(o),
    r,
    ar = [],
    e;
  try {
    while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
  } catch (error) {
    e = {
      error: error
    };
  } finally {
    try {
      if (r && !r.done && (m = i["return"])) m.call(i);
    } finally {
      if (e) throw e.error;
    }
  }
  return ar;
};
function serializeKeyPairs(keyPairs) {
  return keyPairs.reduce(function (hValue, current) {
    var value = "" + hValue + (hValue !== '' ? BAGGAGE_ITEMS_SEPARATOR : '') + current;
    return value.length > BAGGAGE_MAX_TOTAL_LENGTH ? hValue : value;
  }, '');
}
function getKeyPairs(baggage) {
  return baggage.getAllEntries().map(function (_a) {
    var _b = __read(_a, 2),
      key = _b[0],
      value = _b[1];
    var entry = encodeURIComponent(key) + "=" + encodeURIComponent(value.value);
    if (value.metadata !== undefined) {
      entry += BAGGAGE_PROPERTIES_SEPARATOR + value.metadata.toString();
    }
    return entry;
  });
}
function parsePairKeyValue(entry) {
  var valueProps = entry.split(BAGGAGE_PROPERTIES_SEPARATOR);
  if (valueProps.length <= 0) return;
  var keyPairPart = valueProps.shift();
  if (!keyPairPart) return;
  var separatorIndex = keyPairPart.indexOf(BAGGAGE_KEY_PAIR_SEPARATOR);
  if (separatorIndex <= 0) return;
  var key = decodeURIComponent(keyPairPart.substring(0, separatorIndex).trim());
  var value = decodeURIComponent(keyPairPart.substring(separatorIndex + 1).trim());
  var metadata;
  if (valueProps.length > 0) {
    metadata = baggageEntryMetadataFromString(valueProps.join(BAGGAGE_PROPERTIES_SEPARATOR));
  }
  return {
    key: key,
    value: value,
    metadata: metadata
  };
}

var W3CBaggagePropagator = function () {
  function W3CBaggagePropagator() {}
  W3CBaggagePropagator.prototype.inject = function (context, carrier, setter) {
    var baggage = propagation.getBaggage(context);
    if (!baggage || isTracingSuppressed(context)) return;
    var keyPairs = getKeyPairs(baggage).filter(function (pair) {
      return pair.length <= BAGGAGE_MAX_PER_NAME_VALUE_PAIRS;
    }).slice(0, BAGGAGE_MAX_NAME_VALUE_PAIRS);
    var headerValue = serializeKeyPairs(keyPairs);
    if (headerValue.length > 0) {
      setter.set(carrier, BAGGAGE_HEADER, headerValue);
    }
  };
  W3CBaggagePropagator.prototype.extract = function (context, carrier, getter) {
    var headerValue = getter.get(carrier, BAGGAGE_HEADER);
    var baggageString = Array.isArray(headerValue) ? headerValue.join(BAGGAGE_ITEMS_SEPARATOR) : headerValue;
    if (!baggageString) return context;
    var baggage = {};
    if (baggageString.length === 0) {
      return context;
    }
    var pairs = baggageString.split(BAGGAGE_ITEMS_SEPARATOR);
    pairs.forEach(function (entry) {
      var keyPair = parsePairKeyValue(entry);
      if (keyPair) {
        var baggageEntry = {
          value: keyPair.value
        };
        if (keyPair.metadata) {
          baggageEntry.metadata = keyPair.metadata;
        }
        baggage[keyPair.key] = baggageEntry;
      }
    });
    if (Object.entries(baggage).length === 0) {
      return context;
    }
    return propagation.setBaggage(context, propagation.createBaggage(baggage));
  };
  W3CBaggagePropagator.prototype.fields = function () {
    return [BAGGAGE_HEADER];
  };
  return W3CBaggagePropagator;
}();

var ExportResultCode;
(function (ExportResultCode) {
  ExportResultCode[ExportResultCode["SUCCESS"] = 0] = "SUCCESS";
  ExportResultCode[ExportResultCode["FAILED"] = 1] = "FAILED";
})(ExportResultCode || (ExportResultCode = {}));

var __values = undefined && undefined.__values || function (o) {
  var s = typeof Symbol === "function" && Symbol.iterator,
    m = s && o[s],
    i = 0;
  if (m) return m.call(o);
  if (o && typeof o.length === "number") return {
    next: function next() {
      if (o && i >= o.length) o = void 0;
      return {
        value: o && o[i++],
        done: !o
      };
    }
  };
  throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var CompositePropagator = function () {
  function CompositePropagator(config) {
    if (config === void 0) {
      config = {};
    }
    var _a;
    this._propagators = (_a = config.propagators) !== null && _a !== void 0 ? _a : [];
    this._fields = Array.from(new Set(this._propagators.map(function (p) {
      return typeof p.fields === 'function' ? p.fields() : [];
    }).reduce(function (x, y) {
      return x.concat(y);
    }, [])));
  }
  CompositePropagator.prototype.inject = function (context, carrier, setter) {
    var e_1, _a;
    try {
      for (var _b = __values(this._propagators), _c = _b.next(); !_c.done; _c = _b.next()) {
        var propagator = _c.value;
        try {
          propagator.inject(context, carrier, setter);
        } catch (err) {
          diag.warn("Failed to inject with " + propagator.constructor.name + ". Err: " + err.message);
        }
      }
    } catch (e_1_1) {
      e_1 = {
        error: e_1_1
      };
    } finally {
      try {
        if (_c && !_c.done && (_a = _b["return"])) _a.call(_b);
      } finally {
        if (e_1) throw e_1.error;
      }
    }
  };
  CompositePropagator.prototype.extract = function (context, carrier, getter) {
    return this._propagators.reduce(function (ctx, propagator) {
      try {
        return propagator.extract(ctx, carrier, getter);
      } catch (err) {
        diag.warn("Failed to inject with " + propagator.constructor.name + ". Err: " + err.message);
      }
      return ctx;
    }, context);
  };
  CompositePropagator.prototype.fields = function () {
    return this._fields.slice();
  };
  return CompositePropagator;
}();

var VALID_KEY_CHAR_RANGE = '[_0-9a-z-*/]';
var VALID_KEY = "[a-z]" + VALID_KEY_CHAR_RANGE + "{0,255}";
var VALID_VENDOR_KEY = "[a-z0-9]" + VALID_KEY_CHAR_RANGE + "{0,240}@[a-z]" + VALID_KEY_CHAR_RANGE + "{0,13}";
var VALID_KEY_REGEX = new RegExp("^(?:" + VALID_KEY + "|" + VALID_VENDOR_KEY + ")$");
var VALID_VALUE_BASE_REGEX = /^[ -~]{0,255}[!-~]$/;
var INVALID_VALUE_COMMA_EQUAL_REGEX = /,|=/;
function validateKey(key) {
  return VALID_KEY_REGEX.test(key);
}
function validateValue(value) {
  return VALID_VALUE_BASE_REGEX.test(value) && !INVALID_VALUE_COMMA_EQUAL_REGEX.test(value);
}

var MAX_TRACE_STATE_ITEMS = 32;
var MAX_TRACE_STATE_LEN = 512;
var LIST_MEMBERS_SEPARATOR = ',';
var LIST_MEMBER_KEY_VALUE_SPLITTER = '=';
var TraceState = function () {
  function TraceState(rawTraceState) {
    this._internalState = new Map();
    if (rawTraceState) this._parse(rawTraceState);
  }
  TraceState.prototype.set = function (key, value) {
    var traceState = this._clone();
    if (traceState._internalState.has(key)) {
      traceState._internalState["delete"](key);
    }
    traceState._internalState.set(key, value);
    return traceState;
  };
  TraceState.prototype.unset = function (key) {
    var traceState = this._clone();
    traceState._internalState["delete"](key);
    return traceState;
  };
  TraceState.prototype.get = function (key) {
    return this._internalState.get(key);
  };
  TraceState.prototype.serialize = function () {
    var _this = this;
    return this._keys().reduce(function (agg, key) {
      agg.push(key + LIST_MEMBER_KEY_VALUE_SPLITTER + _this.get(key));
      return agg;
    }, []).join(LIST_MEMBERS_SEPARATOR);
  };
  TraceState.prototype._parse = function (rawTraceState) {
    if (rawTraceState.length > MAX_TRACE_STATE_LEN) return;
    this._internalState = rawTraceState.split(LIST_MEMBERS_SEPARATOR).reverse().reduce(function (agg, part) {
      var listMember = part.trim();
      var i = listMember.indexOf(LIST_MEMBER_KEY_VALUE_SPLITTER);
      if (i !== -1) {
        var key = listMember.slice(0, i);
        var value = listMember.slice(i + 1, part.length);
        if (validateKey(key) && validateValue(value)) {
          agg.set(key, value);
        }
      }
      return agg;
    }, new Map());
    if (this._internalState.size > MAX_TRACE_STATE_ITEMS) {
      this._internalState = new Map(Array.from(this._internalState.entries()).reverse().slice(0, MAX_TRACE_STATE_ITEMS));
    }
  };
  TraceState.prototype._keys = function () {
    return Array.from(this._internalState.keys()).reverse();
  };
  TraceState.prototype._clone = function () {
    var traceState = new TraceState();
    traceState._internalState = new Map(this._internalState);
    return traceState;
  };
  return TraceState;
}();

var TRACE_PARENT_HEADER = 'traceparent';
var TRACE_STATE_HEADER = 'tracestate';
var VERSION = '00';
var VERSION_PART = '(?!ff)[\\da-f]{2}';
var TRACE_ID_PART = '(?![0]{32})[\\da-f]{32}';
var PARENT_ID_PART = '(?![0]{16})[\\da-f]{16}';
var FLAGS_PART = '[\\da-f]{2}';
var TRACE_PARENT_REGEX = new RegExp("^\\s?(" + VERSION_PART + ")-(" + TRACE_ID_PART + ")-(" + PARENT_ID_PART + ")-(" + FLAGS_PART + ")(-.*)?\\s?$");
function parseTraceParent(traceParent) {
  var match = TRACE_PARENT_REGEX.exec(traceParent);
  if (!match) return null;
  if (match[1] === '00' && match[5]) return null;
  return {
    traceId: match[2],
    spanId: match[3],
    traceFlags: parseInt(match[4], 16)
  };
}
var W3CTraceContextPropagator = function () {
  function W3CTraceContextPropagator() {}
  W3CTraceContextPropagator.prototype.inject = function (context, carrier, setter) {
    var spanContext = trace.getSpanContext(context);
    if (!spanContext || isTracingSuppressed(context) || !isSpanContextValid(spanContext)) return;
    var traceParent = VERSION + "-" + spanContext.traceId + "-" + spanContext.spanId + "-0" + Number(spanContext.traceFlags || TraceFlags.NONE).toString(16);
    setter.set(carrier, TRACE_PARENT_HEADER, traceParent);
    if (spanContext.traceState) {
      setter.set(carrier, TRACE_STATE_HEADER, spanContext.traceState.serialize());
    }
  };
  W3CTraceContextPropagator.prototype.extract = function (context, carrier, getter) {
    var traceParentHeader = getter.get(carrier, TRACE_PARENT_HEADER);
    if (!traceParentHeader) return context;
    var traceParent = Array.isArray(traceParentHeader) ? traceParentHeader[0] : traceParentHeader;
    if (typeof traceParent !== 'string') return context;
    var spanContext = parseTraceParent(traceParent);
    if (!spanContext) return context;
    spanContext.isRemote = true;
    var traceStateHeader = getter.get(carrier, TRACE_STATE_HEADER);
    if (traceStateHeader) {
      var state = Array.isArray(traceStateHeader) ? traceStateHeader.join(',') : traceStateHeader;
      spanContext.traceState = new TraceState(typeof state === 'string' ? state : undefined);
    }
    return trace.setSpanContext(context, spanContext);
  };
  W3CTraceContextPropagator.prototype.fields = function () {
    return [TRACE_PARENT_HEADER, TRACE_STATE_HEADER];
  };
  return W3CTraceContextPropagator;
}();

var _a$1;
var MAX_RUM_EVENTS_PER_REQUEST = 500;
var MAX_RUM_EVENTS = 20000;
var MAX_MS_RECORD_TIME = 60000;
var DEFAULT_RUM_EVENTS = 5000;
var DEFAULT_MS_RECORD_TIME = 10000;
({
    instrumentationsToSend: (_a$1 = {},
        _a$1[CoralogixEventType.WEB_VITALS] = true,
        _a$1),
});

var Request = (function () {
    function Request(requestConfig) {
        this.requestConfig = requestConfig;
        this.resolvedUrl = '';
        this.resolvedHeaders = {};
        this.init();
    }
    Request.prototype.init = function () {
        var _a = getSdkConfig(), proxyUrl = _a.proxyUrl, coralogixDomain = _a.coralogixDomain, public_key = _a.public_key;
        var _b = this.requestConfig, suffix = _b.suffix, headers = _b.headers;
        var cxEndpoint = "".concat(CoralogixDomainsApiUrlMap[coralogixDomain]).concat(suffix);
        this.resolvedUrl = proxyUrl
            ? "".concat(proxyUrl, "?").concat(PROXY_CX_FORWARD_PARAMETER, "=").concat(encodeURIComponent(cxEndpoint))
            : cxEndpoint;
        if (public_key) {
            headers['Authorization'] = "Bearer ".concat(public_key);
        }
        this.resolvedHeaders = headers;
    };
    Request.prototype.send = function (body) {
        return fetch(this.resolvedUrl, {
            method: 'POST',
            headers: this.resolvedHeaders,
            body: body,
        }).catch(function (e) {
            return e;
        });
    };
    return Request;
}());

var CxCustomSampler = (function () {
    function CxCustomSampler() {
    }
    CxCustomSampler.prototype.shouldSample = function () {
        return {
            decision: SamplingDecision.RECORD,
        };
    };
    return CxCustomSampler;
}());

function resolveMaxRumEvents(maxRumEvents) {
    if (!maxRumEvents) {
        return DEFAULT_RUM_EVENTS;
    }
    return maxRumEvents > MAX_RUM_EVENTS ? MAX_RUM_EVENTS : maxRumEvents;
}
function resolveMaxRecordTime(maxRecordTime) {
    if (!maxRecordTime) {
        return DEFAULT_MS_RECORD_TIME;
    }
    return maxRecordTime > MAX_MS_RECORD_TIME
        ? MAX_MS_RECORD_TIME
        : maxRecordTime;
}
function resolveSessionWithErrorsConfig(config) {
    var _a;
    var _b;
    if (!config) {
        return { enable: false };
    }
    return __assign(__assign({}, config), { maxRumEvents: resolveMaxRumEvents(config.maxRumEvents), maxRecordTime: resolveMaxRecordTime(config.maxRecordTime), instrumentationsToSend: (_b = config.instrumentationsToSend) !== null && _b !== void 0 ? _b : (_a = {},
            _a[CoralogixEventType.WEB_VITALS] = true,
            _a) });
}
function resolveSessionConfig(config) {
    var _a;
    if (!config) {
        return DEFAULT_SESSION_CONFIG;
    }
    return __assign(__assign({}, config), { sessionSampleRate: (_a = config.sessionSampleRate) !== null && _a !== void 0 ? _a : 100, onlyWithErrorConfig: resolveSessionWithErrorsConfig(config.onlyWithErrorConfig) });
}
function resolveSessionRecordingConfig(config) {
    var _a;
    if (config) {
        return __assign(__assign({}, config), { sessionRecordingSampleRate: (_a = config.sessionRecordingSampleRate) !== null && _a !== void 0 ? _a : 100 });
    }
    return;
}
function isSamplingOn(threshold) {
    return threshold > 0 && Math.random() * 100 <= threshold;
}
var isSessionWithError = function () {
    var _a = getSessionManager(), onlySessionWithErrorMode = _a.onlySessionWithErrorMode, cachedLogsSent = _a.cachedLogsSent;
    return onlySessionWithErrorMode && !cachedLogsSent;
};
function getResolvedSampler(resolvedOptions) {
    var sampler = new AlwaysOnSampler();
    var sessionSampleRate = resolvedOptions.sessionSampleRate, sessionConfig = resolvedOptions.sessionConfig, debug = resolvedOptions.debug, sessionRecordingConfig = resolvedOptions.sessionRecordingConfig;
    var shouldNotTrackSession = !isSamplingOn(sessionSampleRate) ||
        !isSamplingOn(sessionConfig.sessionSampleRate);
    if (shouldNotTrackSession) {
        var shouldAlwaysTrackSessionsWithErrors = sessionConfig === null || sessionConfig === void 0 ? void 0 : sessionConfig.alwaysTrackSessionsWithErrors;
        if (shouldAlwaysTrackSessionsWithErrors) {
            sessionConfig.onlyWithErrorConfig = __assign(__assign({}, sessionConfig.onlyWithErrorConfig), { enable: true });
        }
        else {
            if (debug) {
                console.debug('CoralogixRum: Session tracking is disabled due to sampling rate');
            }
            sampler = new CxCustomSampler();
            if (sessionRecordingConfig) {
                sessionRecordingConfig.enable = false;
            }
            resolvedOptions.instrumentations = __assign(__assign({}, resolvedOptions.instrumentations), { errors: false, custom: false, web_vitals: false, interactions: false, long_tasks: false, resources: false });
        }
    }
    return sampler;
}

function isProcessorShouldStop(isActive) {
    var _a;
    return !isActive || !!((_a = getSessionManager()) === null || _a === void 0 ? void 0 : _a.isIdleActive);
}

var CoralogixExporter = (function () {
    function CoralogixExporter() {
        var _this = this;
        this.isActive = true;
        this.sdkConfig = getSdkConfig();
        this.request = new Request({
            suffix: CORALOGIX_LOGS_URL_SUFFIX,
            headers: { 'Content-Type': 'application/json' },
        });
        this.batchTimeDelay = BATCH_TIME_DELAY;
        this.invokeLogRequest = function (logs, markCachedLogsSent) {
            if (logs === null || logs === void 0 ? void 0 : logs.length) {
                _this.request
                    .send(JSON.stringify({
                    logs: logs,
                    skip_enrichment_with_ip: !_this.sdkConfig.collectIPData,
                }))
                    .then(function () {
                    if (markCachedLogsSent) {
                        getSessionManager().cachedLogsSent = true;
                    }
                });
            }
        };
    }
    CoralogixExporter.prototype.export = function (spans, resultCallback) {
        if (isProcessorShouldStop(this.isActive)) {
            resultCallback({ code: ExportResultCode.SUCCESS });
            return;
        }
        var currentSpans = spans.reduce(function (cxSpans, span) {
            if (span[CX_MAPPED_SPAN]) {
                cxSpans.push(span[CX_MAPPED_SPAN]);
            }
            return cxSpans;
        }, []);
        if (isSessionWithError()) {
            this.handleSessionWithError(currentSpans);
        }
        else {
            this.invokeLogRequest(currentSpans);
        }
        resultCallback({ code: ExportResultCode.SUCCESS });
    };
    CoralogixExporter.prototype.handleSessionWithError = function (currentSpans) {
        var _this = this;
        var _a = getSessionManager(), maxRumEventsForSessionWithError = _a.maxRumEventsForSessionWithError, instrumentationsToSend = _a.instrumentationsToSend, sessionHasError = _a.sessionHasError;
        var instrumentationLogs = [];
        currentSpans = currentSpans.reduce(function (cxSpans, current) {
            if (instrumentationsToSend &&
                instrumentationsToSend[current.text.cx_rum.event_context.type]) {
                instrumentationLogs.push(current);
            }
            else {
                cxSpans.push(current);
            }
            return cxSpans;
        }, []);
        this.invokeLogRequest(instrumentationLogs);
        var cachedLogs = (CxGlobal.cachedLogs = __spreadArray(__spreadArray([], __read$1((CxGlobal.cachedLogs || [])), false), __read$1(currentSpans), false));
        var logsCounter = cachedLogs.length;
        if (logsCounter > maxRumEventsForSessionWithError && !sessionHasError) {
            this.clearCachedDataForSessionWithError();
        }
        else if (sessionHasError) {
            if (logsCounter > MAX_RUM_EVENTS_PER_REQUEST) {
                var chunks = [];
                for (var i = 0; i < logsCounter; i += MAX_RUM_EVENTS_PER_REQUEST) {
                    chunks.push(cachedLogs.slice(i, i + MAX_RUM_EVENTS_PER_REQUEST));
                }
                chunks.forEach(function (chunk) {
                    setTimeout(function () { return _this.invokeLogRequest(chunk, true); }, _this.batchTimeDelay);
                    _this.batchTimeDelay += BATCH_TIME_DELAY;
                });
                this.clearCachedDataForSessionWithError();
            }
            else {
                this.invokeLogRequest(cachedLogs, true);
                this.clearCachedDataForSessionWithError();
            }
        }
    };
    CoralogixExporter.prototype.clearCachedDataForSessionWithError = function () {
        CxGlobal.cachedLogs = [];
        this.batchTimeDelay = BATCH_TIME_DELAY;
    };
    CoralogixExporter.prototype.shutdown = function () {
        this.isActive = false;
        return Promise.resolve();
    };
    return CoralogixExporter;
}());

var CoralogixWebTracerProvider = (function (_super) {
    __extends(CoralogixWebTracerProvider, _super);
    function CoralogixWebTracerProvider() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    return CoralogixWebTracerProvider;
}(WebTracerProvider));

var SessionIdle = (function () {
    function SessionIdle(idleConfig) {
        var _this = this;
        this.idleConfig = idleConfig;
        this._isIdleActive = false;
        this.stopCallbacks = {};
        this.resetTimeout = function () {
            var _a, _b;
            (_b = (_a = _this.stopCallbacks).clearTimeout) === null || _b === void 0 ? void 0 : _b.call(_a);
            var id = setTimeout(function () {
                _this._isIdleActive = true;
                _this.idleConfig.onIdle();
            }, _this.idleConfig.timeoutDelay);
            _this.stopCallbacks.clearTimeout = function () { return clearTimeout(id); };
        };
        this.idlenessEventsHandler = function () {
            if (_this._isIdleActive) {
                _this._isIdleActive = false;
                _this.idleConfig.onActive();
            }
            _this.resetTimeout();
        };
    }
    Object.defineProperty(SessionIdle.prototype, "isIdleActive", {
        get: function () {
            return this._isIdleActive;
        },
        enumerable: false,
        configurable: true
    });
    SessionIdle.prototype.startIdleListener = function () {
        this.resetTimeout();
        this.trackActivity();
    };
    SessionIdle.prototype.stopIdleListener = function () {
        Object.values(this.stopCallbacks).forEach(function (cb) { return cb(); });
    };
    SessionIdle.prototype.trackActivity = function () {
        var _a, _b;
        (_b = (_a = this.stopCallbacks).removeActivityListeners) === null || _b === void 0 ? void 0 : _b.call(_a);
        if (typeof CxGlobal.addEventListener === 'function') {
            var stop_1 = addEventListeners(CxGlobal, ["touchstart", "keydown", "mousemove"], this.idlenessEventsHandler, { capture: true, passive: true }).stop;
            this.stopCallbacks.removeActivityListeners = stop_1;
        }
    };
    return SessionIdle;
}());

var _a;
var instrumentationsCompatibility = (_a = {},
    _a[XHR_INSTRUMENTATION_NAME] = {
        browsers: [{ name: 'Chrome', minVersion: 43 }],
        warningText: 'XHR',
    },
    _a[SESSION_RECORDING_INSTRUMENTATION_NAME] = {
        browsers: [{ name: 'Chrome', minVersion: 45 }],
        warningText: 'Session Recording',
    },
    _a);
function isInstrumentationCompatible(confKey) {
    var _a = getUserAgentData(), browserVersion = _a.browserVersion, browser = _a.browser;
    var majorVersion = parseInt(browserVersion.split('.')[0]);
    var compatibility = instrumentationsCompatibility[confKey];
    if (!compatibility)
        return true;
    var matchedBrowser = compatibility.browsers.find(function (compatibleBrowser) { return compatibleBrowser.name === browser; });
    if (!matchedBrowser) {
        return true;
    }
    if (!isNaN(majorVersion) && majorVersion < matchedBrowser.minVersion) {
        console.debug("Coralogix Browser SDK - ".concat(compatibility.warningText, " is not supported on this browser"));
        return false;
    }
    return true;
}

var SessionManager = (function (_super) {
    __extends(SessionManager, _super);
    function SessionManager(recordConfig) {
        var _a;
        var _this = _super.call(this, {
            timeoutDelay: SESSION_IDLE_TIME,
            onIdle: function () { return _this.clearSessionWhenIdle(); },
            onActive: function () { return _this.setSession(true); },
        }) || this;
        _this._sessionConfig = getSdkConfig().sessionConfig;
        _this._sessionHasError = false;
        _this._cachedLogsSent = false;
        _this._currentPageTimestamp = getNowTime();
        _this.debugMode = !!((_a = getSdkConfig()) === null || _a === void 0 ? void 0 : _a.debug);
        _this.getPrevSession = function () {
            var _a;
            return JSON.parse((_a = CxGlobal.sessionStorage) === null || _a === void 0 ? void 0 : _a.getItem(PREV_SESSION_KEY));
        };
        _this.getSession = function () {
            var _a;
            var storedSession = JSON.parse((_a = CxGlobal.sessionStorage) === null || _a === void 0 ? void 0 : _a.getItem(SESSION_KEY));
            var now = getNowTime();
            var session;
            if (storedSession) {
                if (storedSession.sessionExpirationDate > now) {
                    session = storedSession;
                }
                else {
                    _this.clearSessionWhenIdle();
                    session = _this.setSession(true);
                }
            }
            return session;
        };
        _this.setSession = function (afterIdle) {
            var _a, _b, _c, _d;
            if (afterIdle === void 0) { afterIdle = false; }
            var shouldkeepSessionAfterReload = (_a = _this._sessionConfig) === null || _a === void 0 ? void 0 : _a.keepSessionAfterReload;
            if (shouldkeepSessionAfterReload) {
                _this.handleRefreshedSessions();
            }
            else {
                _this.createNewSession();
            }
            (_b = CxGlobal.sessionStorage) === null || _b === void 0 ? void 0 : _b.setItem(SESSION_KEY, JSON.stringify(_this.activeSession));
            var sessionRecorder = getSessionRecorder();
            var shouldStartRecordingAfterIdle = afterIdle &&
                !!(sessionRecorder === null || sessionRecorder === void 0 ? void 0 : sessionRecorder.recordingStopDueToTimeout) &&
                !!((_c = _this.sessionRecorderConfig) === null || _c === void 0 ? void 0 : _c.enable);
            if (shouldStartRecordingAfterIdle ||
                (sessionRecorder === null || sessionRecorder === void 0 ? void 0 : sessionRecorder.getIsAutoStartRecording())) {
                (_d = _this.sessionRecorder) === null || _d === void 0 ? void 0 : _d.startRecording();
            }
            return _this.activeSession;
        };
        _this.initializeSession(recordConfig);
        return _this;
    }
    Object.defineProperty(SessionManager.prototype, "currentPageTimestamp", {
        get: function () {
            return this._currentPageTimestamp;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SessionManager.prototype, "currentPageFragment", {
        get: function () {
            return this._currentPageFragment;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SessionManager.prototype, "sessionConfig", {
        get: function () {
            return this._sessionConfig;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SessionManager.prototype, "onlySessionWithErrorMode", {
        get: function () {
            return this._sessionConfig.onlyWithErrorConfig.enable;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SessionManager.prototype, "maxRumEventsForSessionWithError", {
        get: function () {
            return this._sessionConfig.onlyWithErrorConfig.maxRumEvents || 0;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SessionManager.prototype, "maxRecordTimeForSessionWithError", {
        get: function () {
            return this._sessionConfig.onlyWithErrorConfig.maxRecordTime || 0;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SessionManager.prototype, "instrumentationsToSend", {
        get: function () {
            return this._sessionConfig.onlyWithErrorConfig.instrumentationsToSend;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SessionManager.prototype, "cachedLogsSent", {
        get: function () {
            return this._cachedLogsSent;
        },
        set: function (val) {
            this._cachedLogsSent = val;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SessionManager.prototype, "sessionHasError", {
        get: function () {
            return this._sessionHasError;
        },
        set: function (value) {
            this._sessionHasError = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SessionManager.prototype, "fingerPrint", {
        get: function () {
            return this._fingerPrint;
        },
        enumerable: false,
        configurable: true
    });
    SessionManager.prototype.updateCurrentPage = function (fragment) {
        if (this.isIdleActive) {
            this._currentPageFragment = undefined;
            return;
        }
        this._currentPageFragment = fragment;
        this._currentPageTimestamp = getNowTime();
    };
    SessionManager.prototype.getSessionKey = function () {
        return SESSION_KEY;
    };
    SessionManager.prototype.getActiveSession = function () {
        return this.activeSession;
    };
    SessionManager.prototype.start = function () {
        this.startIdleListener();
        this.clearPrevSession();
        this.setSession();
        this.setFingerPrint();
    };
    SessionManager.prototype.stop = function () {
        this.stopIdleListener();
    };
    SessionManager.prototype.initializeSession = function (recordConfig) {
        return __awaiter(this, void 0, void 0, function () {
            var recordingSamplingEnabled, isSessionRecordingCompatible, SessionRecorder_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        CxGlobal[SESSION_MANAGER_KEY] = this;
                        this.sessionRecorderConfig = recordConfig;
                        if (!(recordConfig === null || recordConfig === void 0 ? void 0 : recordConfig.enable)) return [3, 3];
                        recordingSamplingEnabled = isSamplingOn(recordConfig.sessionRecordingSampleRate);
                        isSessionRecordingCompatible = isInstrumentationCompatible(SESSION_RECORDING_INSTRUMENTATION_NAME);
                        if (!(recordingSamplingEnabled && isSessionRecordingCompatible)) return [3, 2];
                        return [4, import('./sessionRecorder.esm.js')];
                    case 1:
                        SessionRecorder_1 = (_a.sent()).SessionRecorder;
                        this.sessionRecorder = new SessionRecorder_1(this, recordConfig);
                        return [3, 3];
                    case 2:
                        if (getSdkConfig().debug) {
                            console.debug('Coralogix Browser SDK - Session recording is disabled');
                        }
                        _a.label = 3;
                    case 3: return [2];
                }
            });
        });
    };
    SessionManager.prototype.clearSessionWhenIdle = function () {
        var _a;
        if ((_a = this.sessionRecorder) === null || _a === void 0 ? void 0 : _a.getSessionHasRecording()) {
            this.sessionRecorder.recordingStopDueToTimeout = true;
        }
        this.clearSession();
    };
    SessionManager.prototype.clearPrevSession = function () {
        var _a;
        (_a = CxGlobal.sessionStorage) === null || _a === void 0 ? void 0 : _a.removeItem(PREV_SESSION_KEY);
    };
    SessionManager.prototype.clearSession = function () {
        var _a, _b, _c, _d, _e, _f;
        if (this.debugMode) {
            console.debug('Coralogix Browser SDK - Session expired, clearing session');
        }
        (_a = CxGlobal.sessionStorage) === null || _a === void 0 ? void 0 : _a.setItem(PREV_SESSION_KEY, JSON.stringify(__assign(__assign({}, this.activeSession), { hasRecording: (_b = this.sessionRecorder) === null || _b === void 0 ? void 0 : _b.getSessionHasRecording(), hasScreenshot: (_c = this.sessionRecorder) === null || _c === void 0 ? void 0 : _c.getSessionHasScreenshot() })));
        this.clearGlobalSpans();
        this.clearSessionWithErrorMode();
        (_d = getSnapshotManager()) === null || _d === void 0 ? void 0 : _d.resetSnapshot();
        this._currentPageFragment = undefined;
        (_e = this.sessionRecorder) === null || _e === void 0 ? void 0 : _e.stopRecording();
        (_f = CxGlobal.sessionStorage) === null || _f === void 0 ? void 0 : _f.removeItem(SESSION_KEY);
    };
    SessionManager.prototype.clearGlobalSpans = function () {
        var _a;
        (_a = getGlobalSpan()) === null || _a === void 0 ? void 0 : _a.endSpan();
        clearGlobalSpan();
        clearGlobalSpanMap();
    };
    SessionManager.prototype.clearSessionWithErrorMode = function () {
        this.sessionHasError = false;
        this.cachedLogsSent = false;
    };
    SessionManager.prototype.handleRefreshedSessions = function () {
        var _a, _b;
        this.activeSession = this.getSession();
        var recordingSegmentMapFromStorage = JSON.parse(((_a = CxGlobal.sessionStorage) === null || _a === void 0 ? void 0 : _a.getItem(SESSION_RECORDER_SEGMENTS_MAP)) || '{}');
        if (this.activeSession) {
            (_b = getSessionRecorder()) === null || _b === void 0 ? void 0 : _b.updateSegmentIndexCounter(recordingSegmentMapFromStorage);
        }
        else {
            this.createNewSession();
        }
    };
    SessionManager.prototype.createNewSession = function () {
        var now = getNowTime();
        if (this.debugMode) {
            console.debug('Coralogix Browser SDK - Creating new session');
        }
        this.activeSession = {
            sessionId: generateUUID(),
            sessionCreationDate: now,
            sessionExpirationDate: now + SESSION_EXPIRATION_TIME,
        };
    };
    SessionManager.prototype.setFingerPrint = function () {
        var generateFingerPrint = function () { return generateUUID(); };
        var storedFingerPrint = CxGlobal.localStorage.getItem(FINGER_PRINT_KEY);
        if (storedFingerPrint) {
            this._fingerPrint = storedFingerPrint;
            return;
        }
        var newFingerPrint = generateFingerPrint();
        CxGlobal.localStorage.setItem(FINGER_PRINT_KEY, newFingerPrint);
        this._fingerPrint = newFingerPrint;
    };
    return SessionManager;
}(SessionIdle));

var reportInternalEvent = function (event, message) {
    var internalInstrumentation = getInternalRumData(INTERNAL_INSTRUMENTATION_NAME);
    internalInstrumentation === null || internalInstrumentation === void 0 ? void 0 : internalInstrumentation.reportInternalEvent(event, message);
};

function handlePropagators(config, tracerProvider) {
    return __awaiter(this, void 0, void 0, function () {
        var propagators, _a, propagateB3TraceHeader, propagateAwsXrayTraceHeader, singleHeader, multiHeader;
        var _b, _c;
        return __generator(this, function (_d) {
            if ((_b = config.traceParentInHeader) === null || _b === void 0 ? void 0 : _b.enabled) {
                propagators = [];
                _a = ((_c = config.traceParentInHeader) === null || _c === void 0 ? void 0 : _c.options) || {}, propagateB3TraceHeader = _a.propagateB3TraceHeader, propagateAwsXrayTraceHeader = _a.propagateAwsXrayTraceHeader;
                if (!propagateAwsXrayTraceHeader && !propagateB3TraceHeader) {
                    propagators = __spreadArray(__spreadArray([], __read$1(propagators), false), [
                        new W3CBaggagePropagator(),
                        new W3CTraceContextPropagator(),
                    ], false);
                }
                if (propagateAwsXrayTraceHeader) {
                    propagators.push(new AWSXRayPropagator());
                }
                if (propagateB3TraceHeader) {
                    singleHeader = propagateB3TraceHeader.singleHeader, multiHeader = propagateB3TraceHeader.multiHeader;
                    if (singleHeader) {
                        propagators.push(new B3Propagator());
                    }
                    if (multiHeader) {
                        propagators.push(new B3Propagator({ injectEncoding: B3InjectEncoding.MULTI_HEADER }));
                    }
                }
                tracerProvider.register({
                    propagator: new CxPropagator(config, new CompositePropagator({
                        propagators: propagators,
                    })),
                });
            }
            return [2];
        });
    });
}
var CxPropagator = (function () {
    function CxPropagator(config, internalPropagator) {
        this.config = config;
        this.internalPropagator = internalPropagator;
    }
    CxPropagator.prototype.extract = function (context, carrier, getter) {
        return this.internalPropagator.extract(context, carrier, getter);
    };
    CxPropagator.prototype.fields = function () {
        return this.internalPropagator.fields();
    };
    CxPropagator.prototype.inject = function (context, carrier, setter) {
        var _a, _b;
        function checkSpanContextChanges() {
            if (shouldAttachSpanToGlobalSpan(CoralogixEventType.NETWORK_REQUEST)) {
                var globalSpan = getGlobalSpan();
                var currentSpanContext = trace.getSpanContext(context);
                var globalTraceId = globalSpan.span.spanContext().traceId;
                if (currentSpanContext) {
                    currentSpanContext.traceId = globalTraceId;
                    context = trace.setSpanContext(context, currentSpanContext);
                }
            }
        }
        var allowedUrls = (_b = (_a = this.config.traceParentInHeader) === null || _a === void 0 ? void 0 : _a.options) === null || _b === void 0 ? void 0 : _b.allowedTracingUrls;
        var shouldInjectContext = true;
        if (allowedUrls) {
            var span = trace.getSpan(context);
            var url = span.attributes[OtelNetworkAttrs.URL];
            shouldInjectContext = isPatternMatch(url, allowedUrls, 'allowedTracingUrls');
        }
        if (shouldInjectContext) {
            checkSpanContextChanges();
            this.internalPropagator.inject(context, carrier, setter);
        }
    };
    return CxPropagator;
}());

function isInitialConfigValid(initConfig) {
    var e_1, _a;
    if (!initConfig) {
        console.warn('CoralogixRum: Initialization configuration missing');
        return;
    }
    if (!initConfig.proxyUrl && !initConfig.public_key) {
        console.warn('CoralogixRum: Public key is missing');
        return;
    }
    try {
        for (var REQUIRED_CONFIG_KEYS_1 = __values$1(REQUIRED_CONFIG_KEYS), REQUIRED_CONFIG_KEYS_1_1 = REQUIRED_CONFIG_KEYS_1.next(); !REQUIRED_CONFIG_KEYS_1_1.done; REQUIRED_CONFIG_KEYS_1_1 = REQUIRED_CONFIG_KEYS_1.next()) {
            var requiredProp = REQUIRED_CONFIG_KEYS_1_1.value;
            if (isNil(initConfig[requiredProp])) {
                console.error("CoralogixRum: Initialization aborted. Please provide the required property \"".concat(requiredProp, "\"."));
                return;
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (REQUIRED_CONFIG_KEYS_1_1 && !REQUIRED_CONFIG_KEYS_1_1.done && (_a = REQUIRED_CONFIG_KEYS_1.return)) _a.call(REQUIRED_CONFIG_KEYS_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return true;
}
function validateAndResolveInitConfig(config) {
    var _a, _b, _c;
    if (!isInitialConfigValid(config)) {
        return;
    }
    var ignoreUrls = config.ignoreUrls, urlBlueprinters = config.urlBlueprinters, proxyUrl = config.proxyUrl;
    var resolvedIgnoreUrls = [];
    ignoreUrls && resolvedIgnoreUrls.push.apply(resolvedIgnoreUrls, __spreadArray([], __read$1(ignoreUrls), false));
    if (proxyUrl)
        resolvedIgnoreUrls.push(new RegExp(proxyUrl));
    OPTIONS_DEFAULTS.ignoreUrls && resolvedIgnoreUrls.push.apply(resolvedIgnoreUrls, __spreadArray([], __read$1(OPTIONS_DEFAULTS.ignoreUrls), false));
    return __assign(__assign(__assign({}, OPTIONS_DEFAULTS), config), { maskInputTypes: (_a = config.maskInputTypes) !== null && _a !== void 0 ? _a : MASK_INPUT_TYPES_DEFAULT, maskClass: (_b = config.maskClass) !== null && _b !== void 0 ? _b : MASK_CLASS_DEFAULT, ignoreUrls: resolvedIgnoreUrls, urlBlueprinters: resolveUrlBlueprinters(urlBlueprinters), sessionSampleRate: (_c = config.sessionSampleRate) !== null && _c !== void 0 ? _c : 100, sessionConfig: resolveSessionConfig(config.sessionConfig), sessionRecordingConfig: resolveSessionRecordingConfig(config.sessionRecordingConfig), memoryUsageConfig: resolveMemoryUsageConfig(config.memoryUsageConfig), trackSoftNavigations: !!config.trackSoftNavigations, supportMfe: !!config.supportMfe });
}
function resolveMemoryUsageConfig(memoryUsageConfig) {
    var _a;
    if (!memoryUsageConfig) {
        return { enabled: false };
    }
    return __assign(__assign({}, memoryUsageConfig), { interval: (_a = memoryUsageConfig.interval) !== null && _a !== void 0 ? _a : MEMORY_USAGE_DEFAULT_INTERVAL });
}
function resolveUrlBlueprinters(urlBlueprinters) {
    if (!urlBlueprinters) {
        return OPTIONS_DEFAULTS.urlBlueprinters;
    }
    var resolvedUrlBlueprinters = {
        pageUrlBlueprinters: [],
        networkUrlBlueprinters: [],
    };
    var concatBlueprinters = function (urlBlueprinters) {
        var _a, _b;
        var _c = urlBlueprinters || {}, pageUrlBlueprinters = _c.pageUrlBlueprinters, networkUrlBlueprinters = _c.networkUrlBlueprinters;
        if (pageUrlBlueprinters) {
            (_a = resolvedUrlBlueprinters.pageUrlBlueprinters).push.apply(_a, __spreadArray([], __read$1(pageUrlBlueprinters), false));
        }
        if (networkUrlBlueprinters) {
            (_b = resolvedUrlBlueprinters.networkUrlBlueprinters).push.apply(_b, __spreadArray([], __read$1(networkUrlBlueprinters), false));
        }
    };
    concatBlueprinters(urlBlueprinters);
    if (OPTIONS_DEFAULTS.urlBlueprinters) {
        concatBlueprinters(OPTIONS_DEFAULTS.urlBlueprinters);
    }
    return resolvedUrlBlueprinters;
}

var SDK_VERSION = '2.9.0';

function shouldDropEvent(cxRumEvent, options) {
    if (isDocumentErrorWithoutMessage(cxRumEvent)) {
        options.debug && console.warn('Document error without message dropped.');
        return true;
    }
    if (isIgnoredError(cxRumEvent, options.ignoreErrors)) {
        options.debug && console.warn('Ignored error dropped.');
        return true;
    }
    return false;
}
function isDocumentErrorWithoutMessage(cxRumEvent) {
    var _a = cxRumEvent || {}, event_context = _a.event_context, error_context = _a.error_context;
    return (event_context.type === CoralogixEventType.ERROR &&
        event_context.source === ErrorSource.DOCUMENT &&
        !(error_context === null || error_context === void 0 ? void 0 : error_context.error_message));
}
function isIgnoredError(cxRumEvent, ignoreErrors) {
    if (!cxRumEvent.error_context || !(ignoreErrors === null || ignoreErrors === void 0 ? void 0 : ignoreErrors.length)) {
        return false;
    }
    var errorMessage = cxRumEvent.error_context.error_message || '';
    return stringMatchesSomePattern(errorMessage, ignoreErrors);
}

function processBeforeSend(cxSpan, options) {
    if (!(options === null || options === void 0 ? void 0 : options.beforeSend)) {
        return cxSpan;
    }
    var cxRumEvent = cxSpan.text.cx_rum;
    var editableCxRumEvent = cxRumEventToEditable(cxRumEvent);
    var beforeSendResult = options.beforeSend(editableCxRumEvent);
    if (!validateBeforeSendResult(beforeSendResult)) {
        return null;
    }
    var _a = beforeSendResult, applicationName = _a.version_metadata.app_name, severity = _a.event_context.severity;
    return __assign(__assign({}, cxSpan), { applicationName: applicationName, severity: severity, text: { cx_rum: editableToCxRumEvent(beforeSendResult, cxRumEvent) } });
}
function validateBeforeSendResult(beforeSendResult) {
    if (beforeSendResult == null)
        return null;
    var requiredKeys = [
        'version_metadata',
        'session_context',
        'page_context',
        'event_context',
    ];
    var isMissingRequiredKey = function () {
        return requiredKeys.some(function (key) { return !(beforeSendResult === null || beforeSendResult === void 0 ? void 0 : beforeSendResult[key]); });
    };
    if (!isObject(beforeSendResult) || isMissingRequiredKey()) {
        console.warn('CoralogixRum: beforeSend must return null, undefined or a valid event.');
        return null;
    }
    return beforeSendResult;
}
function cxRumEventToEditable(cxRumEvent) {
    var _a = cxRumEvent.session_context, user_id = _a.user_id, user_name = _a.user_name, user_email = _a.user_email, user_metadata = _a.user_metadata; cxRumEvent.browser_sdk; cxRumEvent.timestamp; var rest = __rest(cxRumEvent, ["session_context", "browser_sdk", "timestamp"]);
    return __assign(__assign({}, rest), { session_context: { user_id: user_id, user_email: user_email, user_name: user_name, user_metadata: user_metadata } });
}
function editableToCxRumEvent(editable, cxRumEvent) {
    return __assign(__assign(__assign({}, cxRumEvent), editable), { session_context: __assign(__assign({}, cxRumEvent.session_context), editable.session_context) });
}

var CoralogixSpanMapProcessor = (function () {
    function CoralogixSpanMapProcessor() {
        this.sdkConfig = getSdkConfig();
        this.isActive = true;
    }
    CoralogixSpanMapProcessor.prototype.onEnd = function (span) {
        var _a, _b;
        if (isProcessorShouldStop(this.isActive)) {
            return;
        }
        var _c = getSessionManager(), onlySessionWithErrorMode = _c.onlySessionWithErrorMode, getPrevSession = _c.getPrevSession, getSession = _c.getSession, setSession = _c.setSession;
        var session = getSession() || setSession();
        var sessionHasRecording = !!((_a = getSessionRecorder()) === null || _a === void 0 ? void 0 : _a.getSessionHasRecording());
        var sessionHasScreenshot = !!((_b = getSessionRecorder()) === null || _b === void 0 ? void 0 : _b.getSessionHasScreenshot());
        var mappedCxSpan = this._mapToCxSpan(span, session, getPrevSession(), sessionHasRecording, sessionHasScreenshot, onlySessionWithErrorMode);
        if (!shouldDropEvent(mappedCxSpan.text.cx_rum, this.sdkConfig)) {
            var resolvedCxSpan = processBeforeSend(mappedCxSpan, this.sdkConfig);
            if (resolvedCxSpan) {
                var isSeverityError = resolvedCxSpan.severity === CoralogixLogSeverity.Error;
                var sessionHasError = getSessionManager().sessionHasError;
                if (onlySessionWithErrorMode && (isSeverityError || sessionHasError)) {
                    getSessionManager().sessionHasError = true;
                    resolvedCxSpan.text.cx_rum.session_context.hasError = true;
                }
                span[CX_MAPPED_SPAN] = resolvedCxSpan;
            }
        }
    };
    CoralogixSpanMapProcessor.prototype.onStart = function () { };
    CoralogixSpanMapProcessor.prototype.shutdown = function () {
        this.isActive = false;
        return Promise.resolve();
    };
    CoralogixSpanMapProcessor.prototype.forceFlush = function () {
        return Promise.resolve(undefined);
    };
    CoralogixSpanMapProcessor.prototype._mapToCxSpan = function (span, session, prevSession, sessionHasRecording, sessionHasScreenshot, onlySessionWithErrorMode) {
        var _a, _b;
        var _c = session || {}, sessionId = _c.sessionId, sessionCreationDate = _c.sessionCreationDate;
        var _d = prevSession || {}, prevSessionId = _d.sessionId, prevSessionCreationDate = _d.sessionCreationDate, prevSessionHasRecording = _d.hasRecording, prevSessionHasScreenshot = _d.hasScreenshot;
        var attributes = span.attributes, startTime = span.startTime, parentSpanId = span.parentSpanId, name = span.name, endTime = span.endTime, status = span.status, duration = span.duration, kind = span.kind;
        var user_context = JSON.parse(attributes[CoralogixAttributes.USER_CONTEXT]);
        var application_context = JSON.parse(attributes[CoralogixAttributes.APPLICATION_CONTEXT]);
        var application = application_context.application, version = application_context.version;
        var eventType = attributes[CoralogixAttributes.EVENT_TYPE] ||
            span[CoralogixAttributes.EVENT_TYPE];
        var eventTypeContext = this._getEventTypeContext(attributes, eventType, span);
        var _e = span.spanContext(), spanId = _e.spanId, traceId = _e.traceId;
        var resourceAttributes = __assign(__assign({}, (_a = span.resource) === null || _a === void 0 ? void 0 : _a.attributes), { 'service.name': application });
        var fingerPrint = getSessionManager().fingerPrint;
        var labels = JSON.parse(attributes[CoralogixAttributes.CUSTOM_LABELS]);
        var isErrorWithStacktrace = !!((_b = eventTypeContext === null || eventTypeContext === void 0 ? void 0 : eventTypeContext.error_context) === null || _b === void 0 ? void 0 : _b.original_stacktrace);
        var version_metadata = {
            app_name: application,
            app_version: version,
        };
        var severity = (attributes === null || attributes === void 0 ? void 0 : attributes[CoralogixAttributes.SEVERITY]) ||
            CoralogixLogSeverity.Info;
        var timestamp = attributes[CoralogixAttributes.TIMESTAMP] || getNowTime();
        if (timestamp < sessionCreationDate) {
            timestamp = getNowTime();
        }
        var cxSpan = {
            version_metadata: version_metadata,
            applicationName: application,
            subsystemName: 'cx_rum',
            isErrorWithStacktrace: isErrorWithStacktrace,
            severity: severity,
            timestamp: timestamp,
            text: {
                cx_rum: __assign(__assign({ fingerPrint: fingerPrint, platform: 'browser', timestamp: timestamp }, eventTypeContext), { browser_sdk: {
                        version: SDK_VERSION,
                    }, version_metadata: version_metadata, session_context: __assign(__assign(__assign(__assign({}, user_context), { session_id: sessionId, session_creation_date: sessionCreationDate, prev_session: prevSessionId
                            ? {
                                session_id: prevSessionId,
                                session_creation_date: prevSessionCreationDate,
                                hasRecording: prevSessionHasRecording,
                                hasScreenshot: prevSessionHasScreenshot,
                            }
                            : undefined, hasRecording: sessionHasRecording, hasScreenshot: sessionHasScreenshot, user_agent: navigator.userAgent }), getUserAgentData()), { onlyWithErrorMode: onlySessionWithErrorMode }), page_context: span[CoralogixAttributes.PAGE_CONTEXT], isNavigationEvent: !!span[CoralogixAttributes.IS_NAVIGATION_EVENT], event_context: {
                        type: eventType,
                        source: attributes[CoralogixAttributes.SOURCE],
                        severity: severity,
                    }, labels: labels, environment: attributes[CoralogixAttributes.ENVIRONMENT] || '' }),
            },
        };
        if (shouldAddOtelAttr(span)) {
            cxSpan.text.cx_rum.spanId = spanId;
            cxSpan.text.cx_rum.traceId = traceId;
            cxSpan.text.cx_rum.parentSpanId = parentSpanId;
            cxSpan.instrumentation_data = {
                otelSpan: {
                    spanId: spanId,
                    traceId: traceId,
                    parentSpanId: parentSpanId,
                    name: name,
                    attributes: __assign(__assign(__assign({}, flattenAttributes(attributes)), this.resolveLabels(cxSpan.text.cx_rum)), { sessionId: sessionId }),
                    startTime: startTime,
                    endTime: endTime,
                    status: severity === CoralogixLogSeverity.Error
                        ? __assign(__assign({}, status), { code: SpanStatusCode.ERROR }) : status,
                    kind: kind,
                    duration: duration,
                    sessionId: sessionId,
                },
                otelResource: {
                    attributes: resourceAttributes,
                },
            };
        }
        this.resolveContextFromSpan(cxSpan, span, eventType);
        cxSpan.text.cx_rum.labels = this.resolveLabels(cxSpan.text.cx_rum);
        return cxSpan;
    };
    CoralogixSpanMapProcessor.prototype.resolveContextFromSpan = function (cxSpan, span, eventType) {
        switch (eventType) {
            case CoralogixEventType.CUSTOM_MEASUREMENT: {
                cxSpan.text.cx_rum.custom_measurement_context =
                    span[CoralogixAttributes.CUSTOM_MEASUREMENT_CONTEXT];
                break;
            }
            case CoralogixEventType.MEMORY_USAGE: {
                cxSpan.text.cx_rum.memory_usage_context =
                    span[CoralogixAttributes.MEMORY_USAGE_CONTEXT];
                break;
            }
            case CoralogixEventType.SCREENSHOT: {
                cxSpan.text.cx_rum.screenshot_context =
                    span[CoralogixAttributes.SCREENSHOT_CONTEXT];
                break;
            }
        }
    };
    CoralogixSpanMapProcessor.prototype._getEventTypeContext = function (spanAttributes, eventType, span) {
        var _a;
        switch (eventType) {
            case CoralogixEventType.ERROR: {
                var errorMessage = spanAttributes[CoralogixAttributes.ERROR.MESSAGE];
                var errorCustomData = span[ErrorAttributes.DATA];
                var originalStackTrace = span[ErrorAttributes.STACK];
                return {
                    error_context: {
                        error_type: spanAttributes[CoralogixAttributes.ERROR.TYPE],
                        error_message: errorMessage,
                        original_stacktrace: originalStackTrace,
                        error_custom_data: errorCustomData,
                    },
                };
            }
            case CoralogixEventType.NETWORK_REQUEST: {
                var url = spanAttributes[OtelNetworkAttrs.URL];
                var url_blueprint = applyUrlBluePrinters({
                    url: url,
                    blueprinters: (_a = this.sdkConfig.urlBlueprinters) === null || _a === void 0 ? void 0 : _a.networkUrlBlueprinters,
                });
                return {
                    network_request_context: {
                        method: spanAttributes[OtelNetworkAttrs.METHOD],
                        status_code: spanAttributes[OtelNetworkAttrs.STATUS_CODE],
                        url: url,
                        url_blueprint: url_blueprint,
                        fragments: getUrlFragments(url_blueprint),
                        host: spanAttributes[OtelNetworkAttrs.HOST],
                        schema: spanAttributes[OtelNetworkAttrs.SCHEME],
                        status_text: spanAttributes[OtelNetworkAttrs.STATUS_TEXT],
                        duration: hrTimeToMilliseconds(span === null || span === void 0 ? void 0 : span.duration),
                        source: spanAttributes[CoralogixAttributes.SOURCE],
                        response_content_length: spanAttributes[OtelNetworkAttrs.RESPONSE_CONTENT_LENGTH],
                        request_headers: span[NetworkInstrumentationAttributes.REQUEST_HEADERS],
                        request_payload: span[NetworkInstrumentationAttributes.REQUEST_PAYLOAD],
                        response_headers: span[NetworkInstrumentationAttributes.RESPONSE_HEADERS],
                        response_payload: span[NetworkInstrumentationAttributes.RESPONSE_PAYLOAD],
                    },
                };
            }
            case CoralogixEventType.LOG: {
                if (spanAttributes[CoralogixAttributes.LOG]) {
                    var cxLog = JSON.parse(spanAttributes[CoralogixAttributes.LOG]);
                    return {
                        log_context: {
                            message: cxLog.message,
                            data: cxLog === null || cxLog === void 0 ? void 0 : cxLog.data,
                        },
                    };
                }
                return {
                    log_context: {
                        message: '',
                    },
                };
            }
            case CoralogixEventType.INTERNAL: {
                var internalContext = JSON.parse(spanAttributes[CoralogixAttributes.INTERNAL]);
                var _b = internalContext || {}, event_1 = _b.event, data = _b.data;
                return {
                    internal_context: {
                        event: event_1,
                        data: data,
                    },
                };
            }
            case CoralogixEventType.USER_INTERACTION: {
                return {
                    interaction_context: {
                        target_element: spanAttributes[CoralogixAttributes.TARGET_ELEMENT],
                        event_name: spanAttributes[CoralogixAttributes.INTERACTION_EVENT_NAME],
                        target_element_inner_text: spanAttributes[CoralogixAttributes.ELEMENT_INNER_TEXT],
                        element_id: spanAttributes[CoralogixAttributes.ELEMENT_ID],
                        element_classes: spanAttributes[CoralogixAttributes.ELEMENT_CLASSES],
                        target_element_type: spanAttributes[CoralogixAttributes.TARGET_ELEMENT_TYPE],
                    },
                };
            }
            case CoralogixEventType.WEB_VITALS: {
                var webVitalsContext = JSON.parse(spanAttributes[WEB_VITALS_INSTRUMENTATION_NAME]);
                return {
                    web_vitals_context: webVitalsContext,
                };
            }
            case CoralogixEventType.LONG_TASK: {
                var longTaskContext = JSON.parse(spanAttributes[CoralogixEventType.LONG_TASK]);
                return {
                    longtask_context: longTaskContext,
                };
            }
            case CoralogixEventType.RESOURCES: {
                var resourceContext = JSON.parse(spanAttributes[RESOURCES_INSTRUMENTATION_NAME]);
                var fragments = getUrlFragments(resourceContext.name);
                return {
                    resource_context: __assign(__assign({}, resourceContext), { fragments: fragments }),
                };
            }
        }
        return {};
    };
    CoralogixSpanMapProcessor.prototype.resolveLabels = function (cxRumEvent) {
        var _a, _b;
        var allLabels = cxRumEvent.labels;
        var labelProviders = (_a = this.sdkConfig) === null || _a === void 0 ? void 0 : _a.labelProviders;
        if (labelProviders === null || labelProviders === void 0 ? void 0 : labelProviders.length) {
            var pageUrlLabelProviders = getInternalRumData(PAGE_URL_LABEL_PROVIDERS_KEY);
            var networkUrlLabelProviders = getInternalRumData(NETWORK_URL_LABEL_PROVIDERS_KEY);
            var networkRequestFragment = (_b = cxRumEvent.network_request_context) === null || _b === void 0 ? void 0 : _b.fragments;
            var labelsFromProviders = __assign(__assign({}, this.getLabelsByLabelProviders(pageUrlLabelProviders, cxRumEvent, cxRumEvent.page_context.page_fragments)), (networkRequestFragment
                ? this.getLabelsByLabelProviders(networkUrlLabelProviders, cxRumEvent, networkRequestFragment)
                : {}));
            allLabels = __assign(__assign({}, allLabels), labelsFromProviders);
        }
        return allLabels;
    };
    CoralogixSpanMapProcessor.prototype.getLabelsByLabelProviders = function (labelProviders, cxRumEvent, url) {
        var e_1, _a;
        var allLabels = {};
        var cxRumEventClone = deepClone(cxRumEvent);
        try {
            for (var labelProviders_1 = __values$1(labelProviders), labelProviders_1_1 = labelProviders_1.next(); !labelProviders_1_1.done; labelProviders_1_1 = labelProviders_1.next()) {
                var labelProvider = labelProviders_1_1.value;
                allLabels = __assign(__assign({}, allLabels), labelProvider.providerFunc(url, cxRumEventClone));
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (labelProviders_1_1 && !labelProviders_1_1.done && (_a = labelProviders_1.return)) _a.call(labelProviders_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        return allLabels;
    };
    return CoralogixSpanMapProcessor;
}());

var CoralogixSnapshotSpanProcessor = (function () {
    function CoralogixSnapshotSpanProcessor() {
        this.isActive = true;
    }
    CoralogixSnapshotSpanProcessor.prototype.forceFlush = function () {
        return Promise.resolve();
    };
    CoralogixSnapshotSpanProcessor.prototype.onEnd = function (span) {
        if (isProcessorShouldStop(this.isActive) || !span[CX_MAPPED_SPAN]) {
            return;
        }
        var cxSpan = span[CX_MAPPED_SPAN];
        if (this.isSnapshotPrepared(span)) {
            this.createSnapshot(cxSpan);
        }
        else {
            cxSpan.text.cx_rum.isSnapshotEvent = false;
        }
    };
    CoralogixSnapshotSpanProcessor.prototype.onStart = function (span, parentContext) { };
    CoralogixSnapshotSpanProcessor.prototype.shutdown = function () {
        this.isActive = false;
        return Promise.resolve();
    };
    CoralogixSnapshotSpanProcessor.prototype.createSnapshot = function (cxSpan) {
        var _a = getSnapshotManager().currentSnapshot; _a.timestamp; var snapshotContext = __rest(_a, ["timestamp"]);
        cxSpan.text.cx_rum.isSnapshotEvent = true;
        cxSpan.text.cx_rum.snapshot_context = snapshotContext;
    };
    CoralogixSnapshotSpanProcessor.prototype.isSnapshotPrepared = function (span) {
        var _a;
        var _b = span.attributes, _c = CoralogixAttributes.TIMESTAMP, spanTimestamp = _b[_c], _d = CoralogixAttributes.EVENT_TYPE, spanType = _b[_d], _e = CoralogixAttributes.SEVERITY, spanSeverity = _b[_e];
        var shouldCreateSnapshot = false;
        var _f = getSnapshotManager(), fragmentsState = _f.fragmentsState, updateSnapshot = _f.updateSnapshot, currentSnapshot = _f.currentSnapshot, isSnapshotSentDueToRecording = _f.isSnapshotSentDueToRecording;
        var snapshotTimestamp = currentSnapshot.timestamp, actionCount = currentSnapshot.actionCount, errorCount = currentSnapshot.errorCount;
        var pageContext = span[CoralogixAttributes.PAGE_CONTEXT];
        if (pageContext && !fragmentsState.has(pageContext.page_fragments)) {
            fragmentsState.add(pageContext.page_fragments);
            updateSnapshot({
                timestamp: getNowTime(),
                viewCount: fragmentsState.size,
            });
            shouldCreateSnapshot = true;
        }
        if (spanSeverity === CoralogixLogSeverity.Error) {
            updateSnapshot({
                timestamp: getNowTime(),
                errorCount: errorCount + 1,
            });
            shouldCreateSnapshot = true;
        }
        if (((_a = getSessionRecorder()) === null || _a === void 0 ? void 0 : _a.getSessionHasRecording()) &&
            !isSnapshotSentDueToRecording) {
            updateSnapshot({ timestamp: getNowTime(), hasRecording: true });
            getSnapshotManager().isSnapshotSentDueToRecording = true;
            shouldCreateSnapshot = true;
        }
        if (spanType === CoralogixEventType.DOM) {
            updateSnapshot({ timestamp: getNowTime() });
            shouldCreateSnapshot = true;
        }
        if (spanType === CoralogixEventType.SCREENSHOT) {
            updateSnapshot({ hasScreenshot: true });
        }
        if (spanTimestamp - snapshotTimestamp >=
            millisecondsPerMinute) {
            updateSnapshot({ timestamp: getNowTime() });
            shouldCreateSnapshot = true;
        }
        if (spanType === CoralogixEventType.USER_INTERACTION) {
            updateSnapshot({ actionCount: actionCount + 1 });
        }
        return shouldCreateSnapshot;
    };
    return CoralogixSnapshotSpanProcessor;
}());

var SnapshotManager = (function () {
    function SnapshotManager() {
        var _this = this;
        this.updateSnapshot = function (overrides) {
            _this._currentSnapshot = __assign(__assign({}, _this._currentSnapshot), overrides);
        };
        this.resetSnapshot = function () {
            _this._currentSnapshot = __assign(__assign({}, INITIAL_SNAPSHOT_CONTEXT), { timestamp: getNowTime() });
            _this._fragmentsState = new Set();
            _this._isSnapshotSentDueToRecording = false;
        };
        CxGlobal[SNAPSHOT_MANAGER_KEY] = this;
        this.resetSnapshot();
    }
    Object.defineProperty(SnapshotManager.prototype, "fragmentsState", {
        get: function () {
            return this._fragmentsState;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SnapshotManager.prototype, "currentSnapshot", {
        get: function () {
            return this._currentSnapshot;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SnapshotManager.prototype, "isSnapshotSentDueToRecording", {
        get: function () {
            return this._isSnapshotSentDueToRecording;
        },
        set: function (value) {
            this._isSnapshotSentDueToRecording = value;
        },
        enumerable: false,
        configurable: true
    });
    return SnapshotManager;
}());

var CoralogixNavigationProcessor = (function () {
    function CoralogixNavigationProcessor() {
    }
    CoralogixNavigationProcessor.prototype.forceFlush = function () {
        return Promise.resolve();
    };
    CoralogixNavigationProcessor.prototype.onEnd = function (span) { };
    CoralogixNavigationProcessor.prototype.onStart = function (span) {
        var pageContext = resolvePageContext(location.href);
        if (pageContext) {
            span[CoralogixAttributes.PAGE_CONTEXT] = pageContext;
            var currentPageFragment = getSessionManager().currentPageFragment;
            var page_fragments = pageContext.page_fragments;
            if (currentPageFragment !== page_fragments) {
                getSessionManager().updateCurrentPage(page_fragments);
                span[CoralogixAttributes.IS_NAVIGATION_EVENT] = true;
            }
        }
    };
    CoralogixNavigationProcessor.prototype.shutdown = function () {
        return Promise.resolve();
    };
    return CoralogixNavigationProcessor;
}());

function getTiming(duration) {
    if (typeof duration === 'number') {
        return duration;
    }
    return getNowTime() - getSessionManager().currentPageTimestamp;
}

if (supportedPerformanceTypes === null || supportedPerformanceTypes === void 0 ? void 0 : supportedPerformanceTypes.has(PerformanceTypes.LongTask)) {
    var ttiHandler_1 = (CxGlobal.__tti = {
        e: [],
    });
    ttiHandler_1.o = new PerformanceObserver(function (list) {
        ttiHandler_1.e = ttiHandler_1.e.concat(list.getEntries());
    });
    observeIfSupported(ttiHandler_1.o, PerformanceTypes.LongTask);
}
var isInited = false;
var _deregisterInstrumentations;
var _customInstrumentation;
var errorsInstrumentation;
var customMeasurementInstrumentation;
var memoryUsageInstrumentation;
var screenshotInstrumentation;
var tracerProvider;
var attributesProcessor;
var snapshotProcessor;
var spanMapProcessor;
var navigationProcessor;
var exporter;
var timeMeasurementTracker;
var CoralogixRum = {
    get isInited() {
        return isInited;
    },
    init: function (options) {
        var _a, _b;
        var _this = this;
        try {
            if (isInited) {
                if (options === null || options === void 0 ? void 0 : options.debug) {
                    console.debug('CoralogixRum already initialized, skipping init');
                }
                return;
            }
            if (!CxGlobal.sessionStorage) {
                console.warn('CoralogixRum: Non-browser environment detected, aborting');
                return;
            }
            if (typeof Symbol !== 'function') {
                console.warn('CoralogixRum: browser not supported, disabling instrumentation.');
                return;
            }
            var resolvedOptions_1 = validateAndResolveInitConfig(options);
            if (!resolvedOptions_1) {
                return;
            }
            CxGlobal[USER_AGENT_KEY] = parseUserAgent(navigator.userAgent);
            CxGlobal[SDK_CONFIG_KEY] = resolvedOptions_1;
            var sampler = getResolvedSampler(resolvedOptions_1);
            if (!resolvedOptions_1.debug && !resolvedOptions_1.public_key) {
                console.warn('rumAuth will be required in the future');
            }
            timeMeasurementTracker = new TimeMeasurementTracker();
            new SessionManager(resolvedOptions_1.sessionRecordingConfig).start();
            var ignoreUrls_1 = resolvedOptions_1.ignoreUrls, labels = resolvedOptions_1.labels, user_context = resolvedOptions_1.user_context, application = resolvedOptions_1.application, version = resolvedOptions_1.version, traceParentInHeader_1 = resolvedOptions_1.traceParentInHeader, environment = resolvedOptions_1.environment;
            tracerProvider = new CoralogixWebTracerProvider({
                sampler: sampler,
            });
            var pluginDefaults_1 = {};
            var instrumentations = INSTRUMENTATIONS.map(function (_a) {
                var _b, _c;
                var Instrument = _a.Instrument, confKey = _a.confKey, disable = _a.disable;
                var pluginConf = getInstrumentationConfig((_b = resolvedOptions_1 === null || resolvedOptions_1 === void 0 ? void 0 : resolvedOptions_1.instrumentations) === null || _b === void 0 ? void 0 : _b[confKey], pluginDefaults_1, disable);
                if (pluginConf) {
                    var instrumentation = void 0;
                    switch (confKey) {
                        case CoralogixEventType.WORKER: {
                            if (resolvedOptions_1.workerSupport) {
                                instrumentation = new Instrument(pluginConf, errorsInstrumentation, _this);
                            }
                            break;
                        }
                        case ERROR_INSTRUMENTATION_NAME: {
                            instrumentation = new Instrument(pluginConf);
                            errorsInstrumentation =
                                instrumentation;
                            break;
                        }
                        case FETCH_INSTRUMENTATION_NAME:
                        case XHR_INSTRUMENTATION_NAME: {
                            if (confKey === XHR_INSTRUMENTATION_NAME) {
                                var isXhrCompatible = isInstrumentationCompatible(XHR_INSTRUMENTATION_NAME);
                                if (!isXhrCompatible) {
                                    return;
                                }
                            }
                            instrumentation = new Instrument(__assign(__assign({}, pluginConf), { ignoreUrls: ignoreUrls_1, traceParentInHeader: traceParentInHeader_1 }));
                            break;
                        }
                        case CoralogixEventType.INTERNAL: {
                            instrumentation = new Instrument(pluginConf);
                            saveInternalRumData(INTERNAL_INSTRUMENTATION_NAME, instrumentation);
                            break;
                        }
                        case CoralogixEventType.SCREENSHOT: {
                            if ((_c = resolvedOptions_1.sessionRecordingConfig) === null || _c === void 0 ? void 0 : _c.enable) {
                                instrumentation = new Instrument(pluginConf);
                                screenshotInstrumentation =
                                    instrumentation;
                            }
                            break;
                        }
                        case CoralogixEventType.MEMORY_USAGE: {
                            if (resolvedOptions_1.memoryUsageConfig.enabled &&
                                isMeasureUserAgentSpecificMemoryAllowed()) {
                                instrumentation = new Instrument(pluginConf);
                                memoryUsageInstrumentation =
                                    instrumentation;
                            }
                            break;
                        }
                        default:
                            instrumentation = new Instrument(pluginConf);
                    }
                    if (instrumentation instanceof CoralogixCustomLogInstrumentation) {
                        _customInstrumentation = instrumentation;
                    }
                    if (instrumentation instanceof
                        CoralogixCustomMeasurementInstrumentation) {
                        customMeasurementInstrumentation = instrumentation;
                    }
                    return instrumentation;
                }
                return null;
            }).filter(function (instrument) {
                return Boolean(instrument);
            });
            var mergedUserContext = __assign(__assign({}, OPTIONS_DEFAULTS.user_context), user_context);
            attributesProcessor = new CoralogixSpanAttributesProcessor(__assign((_a = {}, _a[CoralogixAttributes.APPLICATION_CONTEXT] = JSON.stringify({
                application: application,
                version: version,
            }), _a[CoralogixAttributes.USER_CONTEXT] = JSON.stringify(mergedUserContext), _a), (environment
                ? (_b = {}, _b[CoralogixAttributes.ENVIRONMENT] = environment, _b) : {})));
            if (labels) {
                attributesProcessor.setCustomLabels(labels);
            }
            setAttrProcessor(attributesProcessor);
            tracerProvider.addSpanProcessor(attributesProcessor);
            navigationProcessor = new CoralogixNavigationProcessor();
            tracerProvider.addSpanProcessor(navigationProcessor);
            spanMapProcessor = new CoralogixSpanMapProcessor();
            tracerProvider.addSpanProcessor(spanMapProcessor);
            new SnapshotManager();
            snapshotProcessor = new CoralogixSnapshotSpanProcessor();
            tracerProvider.addSpanProcessor(snapshotProcessor);
            tracerProvider.addSpanProcessor(new BatchSpanProcessor((exporter = new CoralogixExporter()), {
                maxExportBatchSize: MAX_EXPORT_BATCH_SIZE,
                scheduledDelayMillis: SCHEDULE_DELAY_MILLIS,
            }));
            handlePropagators(options, tracerProvider);
            _deregisterInstrumentations = registerInstrumentations({
                tracerProvider: tracerProvider,
                instrumentations: instrumentations,
            });
            var _c = __read$1(partition(resolvedOptions_1.labelProviders || [], function (_a) {
                var urlType = _a.urlType;
                return urlType === UrlType.PAGE || !urlType;
            }), 2), pageUrlLabelProviders = _c[0], networkUrlLabelProviders = _c[1];
            saveInternalRumData(PAGE_URL_LABEL_PROVIDERS_KEY, pageUrlLabelProviders);
            saveInternalRumData(NETWORK_URL_LABEL_PROVIDERS_KEY, networkUrlLabelProviders);
            isInited = true;
            if (options === null || options === void 0 ? void 0 : options.debug) {
                console.debug('Coralogix Browser SDK - initialization has completed');
            }
            reportInternalEvent('init');
        }
        catch (err) {
            console.warn('Coralogix Browser SDK - initialization failed: ', err);
        }
    },
    getCustomTracer: function (ignoredList) {
        var _a, _b;
        if (!isInited) {
            console.debug('CoralogixRum must be initiated before getting custom tracer');
            return;
        }
        if (!((_b = (_a = getSdkConfig()) === null || _a === void 0 ? void 0 : _a.traceParentInHeader) === null || _b === void 0 ? void 0 : _b.enabled)) {
            console.debug('Coralogix Browser SDK - TraceParentInHeader is not enabled');
            return;
        }
        if (getActiveCustomTracer()) {
            console.warn('Coralogix Browser SDK - Custom tracer already exists');
            return;
        }
        var tracer = tracerProvider.getTracer(MAIN_TRACER_KEY);
        setCustomTracer(tracer);
        return {
            startGlobalSpan: function (name, labels) {
                if (getGlobalSpan()) {
                    console.warn('Coralogix Browser SDK - Global span already exists');
                    return;
                }
                setCustomTracerIgnoredInstruments((ignoredList === null || ignoredList === void 0 ? void 0 : ignoredList.ignoredInstruments) || []);
                var globalSpan = tracer.startSpan(name);
                globalSpan[CoralogixAttributes.EVENT_TYPE] =
                    CoralogixEventType.CUSTOM_SPAN;
                if (labels) {
                    setCustomLabelsForSpan(globalSpan, labels);
                }
                markSpanAsOtelToSend(globalSpan);
                var globalSpanModel = {
                    span: globalSpan,
                    withContext: function (fn) {
                        return context.with(trace.setSpan(context.active(), globalSpan), fn);
                    },
                    endSpan: function () {
                        globalSpan === null || globalSpan === void 0 ? void 0 : globalSpan.end();
                        clearGlobalSpan();
                    },
                    startCustomSpan: function (name, labels) {
                        var customSpan;
                        context.with(trace.setSpan(context.active(), globalSpan), function () {
                            customSpan = tracer.startSpan(name);
                            customSpan[CoralogixAttributes.EVENT_TYPE] =
                                CoralogixEventType.CUSTOM_SPAN;
                            if (labels) {
                                setCustomLabelsForSpan(customSpan, labels);
                            }
                            markSpanAsOtelToSend(customSpan);
                        });
                        return {
                            span: customSpan,
                            endSpan: function () {
                                customSpan === null || customSpan === void 0 ? void 0 : customSpan.end();
                            },
                        };
                    },
                };
                setGlobalSpan(globalSpanModel);
                addGlobalSpanByTraceId(globalSpan.spanContext().traceId, globalSpanModel);
                return globalSpanModel;
            },
        };
    },
    shutdown: function () {
        var _a, _b;
        if (!isInited) {
            return;
        }
        _deregisterInstrumentations === null || _deregisterInstrumentations === void 0 ? void 0 : _deregisterInstrumentations();
        _deregisterInstrumentations = undefined;
        this.stopSessionRecording();
        (_a = getSessionManager()) === null || _a === void 0 ? void 0 : _a.stop();
        attributesProcessor === null || attributesProcessor === void 0 ? void 0 : attributesProcessor.shutdown();
        navigationProcessor === null || navigationProcessor === void 0 ? void 0 : navigationProcessor.shutdown();
        spanMapProcessor === null || spanMapProcessor === void 0 ? void 0 : spanMapProcessor.shutdown();
        snapshotProcessor === null || snapshotProcessor === void 0 ? void 0 : snapshotProcessor.shutdown();
        exporter === null || exporter === void 0 ? void 0 : exporter.shutdown();
        isInited = false;
        if ((_b = getSdkConfig()) === null || _b === void 0 ? void 0 : _b.debug) {
            console.debug('Coralogix Browser SDK - shutdown has completed');
        }
    },
    setApplicationContext: function (applicationContext) {
        var _a;
        if (!isInited) {
            console.debug('CoralogixRum must be initiated before setting application context');
            return;
        }
        attributesProcessor === null || attributesProcessor === void 0 ? void 0 : attributesProcessor.setInternalLabels(__assign(__assign({}, attributesProcessor.getInternalLabels()), (_a = {}, _a[CoralogixAttributes.APPLICATION_CONTEXT] = applicationContext, _a)));
    },
    captureError: function (error, customData, labels) {
        if (!isInited) {
            console.debug('CoralogixRum must be initiated before reporting errors');
            return;
        }
        errorsInstrumentation === null || errorsInstrumentation === void 0 ? void 0 : errorsInstrumentation.reportError(ErrorSource.CAPTURED, error, customData, labels);
    },
    sendCustomMeasurement: function (name, value) {
        if (!isInited) {
            console.debug('CoralogixRum must be initiated before sending custom measurement');
            return;
        }
        customMeasurementInstrumentation === null || customMeasurementInstrumentation === void 0 ? void 0 : customMeasurementInstrumentation.createCustomMeasurement(name, value);
    },
    addTiming: function (name, duration) {
        if (!isInited) {
            console.debug('CoralogixRum must be initiated before adding timing');
            return;
        }
        this.sendCustomMeasurement(name, getTiming(duration));
    },
    measureUserAgentSpecificMemory: function () {
        if (!isInited) {
            console.debug('CoralogixRum must be initiated before measuring user agent specific memory');
            return;
        }
        if (!memoryUsageInstrumentation) {
            console.debug('Memory usage instrumentation is not enabled');
            return;
        }
        memoryUsageInstrumentation.startTrackingMemoryUsage();
    },
    stopSessionRecording: function () {
        var _a;
        (_a = getSessionRecorder()) === null || _a === void 0 ? void 0 : _a.stopRecording();
    },
    startSessionRecording: function () {
        var _a;
        if (!isInited) {
            if ((_a = getSdkConfig()) === null || _a === void 0 ? void 0 : _a.debug) {
                console.debug('Coralogix Browser SDK - CoralogixRum must be initiated before starting session recording');
            }
            return;
        }
        if (getSessionRecorder()) {
            getSessionRecorder().startRecording();
            return;
        }
        var intervalId = setInterval(function () {
            if (getSessionRecorder()) {
                clearInterval(intervalId);
                getSessionRecorder().startRecording();
            }
        }, 300);
        setTimeout(function () { return clearInterval(intervalId); }, 3000);
    },
    screenshot: function (description) {
        var _a;
        if (!isInited) {
            console.debug('CoralogixRum must be initiated before capturing screenshot');
            return;
        }
        var id = generateUUID();
        screenshotInstrumentation === null || screenshotInstrumentation === void 0 ? void 0 : screenshotInstrumentation.screenshot(id, description);
        (_a = getSessionRecorder()) === null || _a === void 0 ? void 0 : _a.screenshot(id);
        return id;
    },
    setUserContext: function (userContext) {
        var _a;
        if (!isInited) {
            console.debug('CoralogixRum must be initiated before setting user context');
            return;
        }
        attributesProcessor === null || attributesProcessor === void 0 ? void 0 : attributesProcessor.setInternalLabels(__assign(__assign({}, attributesProcessor.getInternalLabels()), (_a = {}, _a[CoralogixAttributes.USER_CONTEXT] = userContext, _a)));
    },
    getUserContext: function () {
        return attributesProcessor === null || attributesProcessor === void 0 ? void 0 : attributesProcessor.getUserContext();
    },
    setLabels: function (labels) {
        attributesProcessor === null || attributesProcessor === void 0 ? void 0 : attributesProcessor.setCustomLabels(labels);
    },
    getLabels: function () {
        return (attributesProcessor === null || attributesProcessor === void 0 ? void 0 : attributesProcessor.getCustomLabels()) || {};
    },
    getSessionId: function () {
        var _a, _b;
        return (_b = (_a = getSessionManager()) === null || _a === void 0 ? void 0 : _a.getSession()) === null || _b === void 0 ? void 0 : _b.sessionId;
    },
    log: function (severity, message, data, labels) {
        if (!isInited) {
            console.debug('CoralogixRum must be initiated before using log');
            return;
        }
        if (!_customInstrumentation) {
            console.warn('Log was reported, but custom instrumentation is disabled.');
            return;
        }
        _customInstrumentation === null || _customInstrumentation === void 0 ? void 0 : _customInstrumentation.log({ severity: severity, message: message, data: data, labels: labels });
    },
    debug: function (message, data, labels) {
        this.log(CoralogixLogSeverity.Debug, message, data, labels);
    },
    verbose: function (message, data, labels) {
        this.log(CoralogixLogSeverity.Verbose, message, data, labels);
    },
    info: function (message, data, labels) {
        this.log(CoralogixLogSeverity.Info, message, data, labels);
    },
    warn: function (message, data, labels) {
        this.log(CoralogixLogSeverity.Warn, message, data, labels);
    },
    error: function (message, data, labels) {
        this.log(CoralogixLogSeverity.Error, message, data, labels);
    },
    critical: function (message, data, labels) {
        this.log(CoralogixLogSeverity.Critical, message, data, labels);
    },
    startTimeMeasure: function (key, labels) {
        if (!isInited) {
            console.debug('CoralogixRum must be initiated before starting time measurement.');
            return;
        }
        timeMeasurementTracker === null || timeMeasurementTracker === void 0 ? void 0 : timeMeasurementTracker.startMeasurement(key, labels);
    },
    endTimeMeasure: function (key) {
        var measurement = timeMeasurementTracker === null || timeMeasurementTracker === void 0 ? void 0 : timeMeasurementTracker.getMeasurement(key);
        if (measurement) {
            var duration = measurement.duration, labels = measurement.labels;
            customMeasurementInstrumentation === null || customMeasurementInstrumentation === void 0 ? void 0 : customMeasurementInstrumentation.createCustomMeasurement(key, duration, getCustomMergedLabels(labels));
        }
    },
};
CxGlobal.CoralogixRum = CoralogixRum;

var UrlBasedLabelProvider = (function () {
    function UrlBasedLabelProvider(config) {
        var urlPatterns = config.urlPatterns, defaultLabels = config.defaultLabels, urlType = config.urlType;
        var compiledRegexps = urlPatterns.map(function (_a) {
            var regexps = _a.regexps;
            return regexps.map(function (regexp) { return new RegExp(regexp); });
        });
        this.urlType = urlType;
        this.providerFunc = function (url, _) {
            var e_1, _a;
            var providerLabels = {};
            try {
                outerLoop: for (var _b = __values$1(compiledRegexps.entries()), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var _d = __read$1(_c.value, 2), index = _d[0], compiledRegex = _d[1];
                    var labels = urlPatterns[index].labels;
                    if (compiledRegex.some(function (regexp) { return regexp.test(url); })) {
                        providerLabels = __assign(__assign({}, providerLabels), labels);
                        break outerLoop;
                    }
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_1) throw e_1.error; }
            }
            var shouldAddDefaultLabels = isEmpty(providerLabels) && isNotEmpty(defaultLabels);
            if (shouldAddDefaultLabels) {
                providerLabels = __assign(__assign({}, providerLabels), defaultLabels);
            }
            return providerLabels;
        };
    }
    return UrlBasedLabelProvider;
}());

var SessionRecordingEventType;
(function (SessionRecordingEventType) {
    SessionRecordingEventType[SessionRecordingEventType["DomContentLoaded"] = 0] = "DomContentLoaded";
    SessionRecordingEventType[SessionRecordingEventType["Load"] = 1] = "Load";
    SessionRecordingEventType[SessionRecordingEventType["FullSnapshot"] = 2] = "FullSnapshot";
    SessionRecordingEventType[SessionRecordingEventType["IncrementalSnapshot"] = 3] = "IncrementalSnapshot";
    SessionRecordingEventType[SessionRecordingEventType["Meta"] = 4] = "Meta";
    SessionRecordingEventType[SessionRecordingEventType["Custom"] = 5] = "Custom";
    SessionRecordingEventType[SessionRecordingEventType["Plugin"] = 6] = "Plugin";
})(SessionRecordingEventType || (SessionRecordingEventType = {}));

export { BATCH_TIME_DELAY as B, CxGlobal as C, MAX_BATCH_TIME_MS as M, OtelNetworkAttrs as O, PerformanceTypes as P, Request as R, SESSION_RECORDER_SEGMENTS_MAP as S, UrlBasedLabelProvider as U, getNowTime as a, SESSION_RECORDING_NETWORK_ERR0R_MESSAGE as b, SESSION_RECORDING_DEFAULT_HEADERS as c, SESSION_RECORDING_POSTFIX_URL as d, SESSION_RECORDER_KEY as e, MAX_MUTATIONS_FOR_SESSION_RECORDING as f, getSdkConfig as g, SESSION_RECORDING_DEFAULT_ERROR_MESSAGE as h, CoralogixRum as i, CoralogixLogSeverity as j, SessionRecordingEventType as k, CoralogixEventType as l, UrlType as m, reportInternalEvent as r };
